import React, { useEffect, useState } from "react";
import { applyBoost, GAME_BOOST } from "../lib/tamagotchiUtils";
import {
  Check,
  X,
  ChevronRight,
  RotateCcw,
  Award,
  Heart,
  Brain,
  Activity,
  Stethoscope,
  Shuffle,
} from "lucide-react";

const SymptomDetective = ({ onBack, userData, saveUserData }) => {
  const storageKey = "symptomDetectiveProgress";
  const [gamePhase, setGamePhase] = useState("specialty"); // specialty, difficulty, playing, complete
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [currentCase, setCurrentCase] = useState(null);
  const [caseIndex, setCaseIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [casesCompleted, setCasesCompleted] = useState(0);
  const [activeCases, setActiveCases] = useState([]);
  const [completedCaseIds, setCompletedCaseIds] = useState({});
  const [resumeSession, setResumeSession] = useState(null);

  const loadSavedProgress = () => {
    if (typeof window === "undefined") return null;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      return null;
    }
  };

  const saveProgress = (nextProgress) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(nextProgress));
    } catch (error) {
      // Ignore storage failures
    }
  };

  // Specialties with their cases
  const specialties = [
    {
      id: "cardiology",
      name: "Cardiology",
      description: "Heart and cardiovascular system",
      icon: Heart,
      color: "from-red-100 to-pink-100",
      borderColor: "border-red-300",
      iconColor: "text-red-600",
    },
    {
      id: "neurology",
      name: "Neurology",
      description: "Brain and nervous system",
      icon: Brain,
      color: "from-purple-100 to-violet-100",
      borderColor: "border-purple-300",
      iconColor: "text-purple-600",
    },
    {
      id: "endocrinology",
      name: "Endocrinology",
      description: "Hormones and metabolism",
      icon: Activity,
      color: "from-teal-100 to-cyan-100",
      borderColor: "border-teal-300",
      iconColor: "text-teal-600",
    },
    {
      id: "general",
      name: "General Medicine",
      description: "Common medical conditions",
      icon: Stethoscope,
      color: "from-blue-100 to-indigo-100",
      borderColor: "border-blue-300",
      iconColor: "text-blue-600",
    },
    {
      id: "random",
      name: "Random Mix",
      description: "All specialties combined",
      icon: Shuffle,
      color: "from-amber-100 to-yellow-100",
      borderColor: "border-amber-300",
      iconColor: "text-amber-600",
    },
  ];

  // Difficulty levels
  const difficultyLevels = [
    {
      id: "easy",
      name: "Medical Student",
      description: "Clear symptoms, straightforward cases",
      color: "from-green-100 to-emerald-100",
      borderColor: "border-green-300",
      coins: 5,
      xp: 10,
    },
    {
      id: "medium",
      name: "Resident",
      description: "Moderate complexity, multiple possibilities",
      color: "from-blue-100 to-cyan-100",
      borderColor: "border-blue-300",
      coins: 10,
      xp: 20,
    },
    {
      id: "hard",
      name: "Specialist",
      description: "Complex cases, subtle differences",
      color: "from-purple-100 to-violet-100",
      borderColor: "border-purple-300",
      coins: 20,
      xp: 40,
    },
  ];

  // Comprehensive medical cases database organized by specialty and difficulty
  const allCases = {
    cardiology: {
      easy: [
        {
          id: "cardio-easy-1",
          patientInfo: "58-year-old male",
          symptoms: [
            "Crushing chest pain radiating to left arm",
            "Shortness of breath",
            "Nausea and sweating",
            "Pain worse with exertion",
            "History of smoking and hypertension",
          ],
          labResults: "Troponin: elevated, ECG: ST elevation",
          correctAnswer: "Myocardial Infarction (Heart Attack)",
          options: [
            "Myocardial Infarction (Heart Attack)",
            "Gastroesophageal Reflux",
            "Panic Attack",
            "Costochondritis",
          ],
          explanation:
            "Elevated troponin with ST elevation on ECG confirms acute myocardial infarction. The chest pain radiating to the left arm, associated with shortness of breath and risk factors (smoking, hypertension) are classic presentations.",
        },
        {
          id: "cardio-easy-2",
          patientInfo: "65-year-old female",
          symptoms: [
            "Swelling in both ankles",
            "Shortness of breath when lying flat",
            "Waking up at night gasping for air",
            "Fatigue and weakness",
            "History of prior heart attack",
          ],
          labResults:
            "BNP: elevated, Echocardiogram: reduced ejection fraction (30%)",
          correctAnswer: "Congestive Heart Failure",
          options: [
            "Congestive Heart Failure",
            "Pulmonary Embolism",
            "Kidney Disease",
            "Sleep Apnea",
          ],
          explanation:
            "Elevated BNP and reduced ejection fraction confirm heart failure. Orthopnea (difficulty breathing when lying flat) and paroxysmal nocturnal dyspnea (waking up gasping) are classic heart failure symptoms.",
        },
        {
          id: "cardio-easy-3",
          patientInfo: "72-year-old male",
          symptoms: [
            "Irregular heartbeat noticed during routine check",
            "Occasional palpitations",
            "Mild fatigue",
            "No chest pain or shortness of breath",
            "History of high blood pressure",
          ],
          labResults: "ECG: irregularly irregular rhythm, no P waves visible",
          correctAnswer: "Atrial Fibrillation",
          options: [
            "Atrial Fibrillation",
            "Premature Ventricular Contractions",
            "Sinus Arrhythmia",
            "Heart Block",
          ],
          explanation:
            "Irregularly irregular rhythm without discernible P waves is pathognomonic for atrial fibrillation. It's the most common sustained arrhythmia, especially in elderly patients with hypertension.",
        },
        {
          id: "cardio-easy-4",
          patientInfo: "55-year-old female",
          symptoms: [
            "Chest discomfort during exercise",
            "Pain goes away with rest",
            "No pain at rest",
            "Mild shortness of breath with exertion",
            "Family history of heart disease",
          ],
          labResults: "Stress test: positive for ischemia, Troponin: normal",
          correctAnswer: "Stable Angina",
          options: [
            "Stable Angina",
            "Unstable Angina",
            "Myocardial Infarction",
            "Musculoskeletal Pain",
          ],
          explanation:
            "Exertional chest pain that resolves with rest and has a positive stress test with normal troponin indicates stable angina. The predictable pattern distinguishes it from unstable angina or MI.",
        },
        {
          id: "cardio-easy-5",
          patientInfo: "28-year-old female",
          symptoms: [
            "Intermittent palpitations",
            "Chest discomfort not related to exertion",
            "Anxiety episodes",
            "Click heard on cardiac exam",
            "Symptoms improve with reassurance",
          ],
          labResults: "Echocardiogram: systolic prolapse of mitral valve leaflet",
          correctAnswer: "Mitral Valve Prolapse",
          options: [
            "Mitral Valve Prolapse",
            "Stable Angina",
            "Atrial Fibrillation",
            "Pericarditis",
          ],
          explanation:
            "Young patient with palpitations and a mid-systolic click with echo showing leaflet prolapse is consistent with mitral valve prolapse.",
        },
        {
          id: "cardio-easy-6",
          patientInfo: "64-year-old male",
          symptoms: [
            "Palpitations with mild dizziness",
            "Irregular but organized heartbeat",
            "No chest pain",
            "History of hypertension",
            "Symptoms started 1 day ago",
          ],
          labResults: "ECG: sawtooth flutter waves, atrial rate ~300 bpm",
          correctAnswer: "Atrial Flutter",
          options: [
            "Atrial Flutter",
            "Atrial Fibrillation",
            "Supraventricular Tachycardia",
            "Premature Atrial Contractions",
          ],
          explanation:
            "Sawtooth flutter waves with an atrial rate around 300 bpm is classic for atrial flutter, which often presents with palpitations and mild dizziness.",
        },
        {
          id: "cardio-easy-7",
          patientInfo: "70-year-old female",
          symptoms: [
            "Sudden onset severe shortness of breath",
            "Pink frothy sputum",
            "Unable to lie flat",
            "Crackles heard throughout lungs",
            "History of hypertension and previous MI",
          ],
          labResults: "Chest X-ray: bilateral pulmonary edema, BNP: markedly elevated",
          correctAnswer: "Acute Pulmonary Edema",
          options: [
            "Acute Pulmonary Edema",
            "Pneumonia",
            "COPD Exacerbation",
            "Pulmonary Embolism",
          ],
          explanation:
            "Pink frothy sputum with bilateral pulmonary edema on chest X-ray and elevated BNP indicates acute pulmonary edema, often a complication of heart failure.",
        },
        {
          id: "cardio-easy-8",
          patientInfo: "52-year-old male",
          symptoms: [
            "High blood pressure reading at routine visit",
            "Occasional headaches",
            "No chest pain or shortness of breath",
            "BMI 32, sedentary lifestyle",
            "Family history of hypertension",
          ],
          labResults: "BP: 158/98 mmHg on multiple readings, normal renal function",
          correctAnswer: "Essential Hypertension",
          options: [
            "Essential Hypertension",
            "Secondary Hypertension",
            "White Coat Hypertension",
            "Pheochromocytoma",
          ],
          explanation:
            "Consistently elevated blood pressure without identifiable secondary causes, combined with risk factors like obesity and family history, suggests essential (primary) hypertension.",
        },
        {
          id: "cardio-easy-9",
          patientInfo: "35-year-old female",
          symptoms: [
            "Episodes of rapid heartbeat",
            "Sudden onset and offset",
            "Mild lightheadedness during episodes",
            "No chest pain",
            "Episodes last 10-30 minutes",
          ],
          labResults: "ECG during episode: narrow complex tachycardia at 180 bpm, regular rhythm",
          correctAnswer: "Supraventricular Tachycardia (SVT)",
          options: [
            "Supraventricular Tachycardia (SVT)",
            "Ventricular Tachycardia",
            "Atrial Fibrillation",
            "Sinus Tachycardia",
          ],
          explanation:
            "Regular narrow complex tachycardia with sudden onset and offset is characteristic of SVT, commonly seen in younger patients without structural heart disease.",
        },
        {
          id: "cardio-easy-10",
          patientInfo: "78-year-old male",
          symptoms: [
            "Fatigue and weakness",
            "Dizziness on standing",
            "Heart rate of 45 bpm",
            "No syncope",
            "Currently on beta-blocker for hypertension",
          ],
          labResults: "ECG: sinus bradycardia, no heart block",
          correctAnswer: "Sinus Bradycardia",
          options: [
            "Sinus Bradycardia",
            "Complete Heart Block",
            "Sick Sinus Syndrome",
            "Atrial Fibrillation with Slow Ventricular Response",
          ],
          explanation:
            "Heart rate below 60 bpm with normal sinus rhythm and P waves indicates sinus bradycardia, which can be caused by beta-blockers.",
        },
        {
          id: "cardio-easy-11",
          patientInfo: "60-year-old male",
          symptoms: [
            "Leg pain when walking",
            "Pain relieved by rest",
            "Cool feet bilaterally",
            "Diminished pedal pulses",
            "History of smoking and diabetes",
          ],
          labResults: "Ankle-brachial index: 0.6 (normal >0.9)",
          correctAnswer: "Peripheral Arterial Disease",
          options: [
            "Peripheral Arterial Disease",
            "Deep Vein Thrombosis",
            "Diabetic Neuropathy",
            "Lumbar Spinal Stenosis",
          ],
          explanation:
            "Claudication (leg pain with walking that resolves with rest), diminished pulses, and low ABI are classic findings of peripheral arterial disease.",
        },
        {
          id: "cardio-easy-12",
          patientInfo: "45-year-old female",
          symptoms: [
            "Swollen, painful left calf",
            "Warmth and redness over calf",
            "Recent long-haul flight",
            "Mild shortness of breath",
            "On oral contraceptives",
          ],
          labResults: "D-dimer: elevated, Ultrasound: non-compressible left popliteal vein",
          correctAnswer: "Deep Vein Thrombosis",
          options: [
            "Deep Vein Thrombosis",
            "Cellulitis",
            "Baker's Cyst Rupture",
            "Muscle Strain",
          ],
          explanation:
            "Unilateral leg swelling with risk factors (immobility, OCP use), elevated D-dimer, and non-compressible vein on ultrasound confirms deep vein thrombosis.",
        },
        {
          id: "cardio-easy-13",
          patientInfo: "68-year-old female",
          symptoms: [
            "Holosystolic murmur at apex",
            "Radiates to left axilla",
            "Progressive fatigue",
            "Mild shortness of breath on exertion",
            "History of rheumatic fever as a child",
          ],
          labResults: "Echocardiogram: severe mitral regurgitation, dilated left atrium",
          correctAnswer: "Mitral Regurgitation",
          options: [
            "Mitral Regurgitation",
            "Mitral Stenosis",
            "Aortic Regurgitation",
            "Tricuspid Regurgitation",
          ],
          explanation:
            "Holosystolic murmur at the apex radiating to the axilla with echo findings of mitral regurgitation. History of rheumatic fever is a common cause.",
        },
        {
          id: "cardio-easy-14",
          patientInfo: "55-year-old male",
          symptoms: [
            "Chest pain at rest",
            "Pain occurred multiple times today",
            "Increasing frequency over past week",
            "Previously had stable angina",
            "Pain lasting longer than before",
          ],
          labResults: "ECG: ST depression in leads V3-V6, Troponin: mildly elevated",
          correctAnswer: "Unstable Angina / NSTEMI",
          options: [
            "Unstable Angina / NSTEMI",
            "Stable Angina",
            "STEMI",
            "Musculoskeletal Pain",
          ],
          explanation:
            "New-onset rest pain with increasing frequency, ST depression, and elevated troponin indicates acute coronary syndrome (unstable angina or NSTEMI).",
        },
        {
          id: "cardio-easy-15",
          patientInfo: "40-year-old male",
          symptoms: [
            "Bounding pulses",
            "Wide pulse pressure",
            "Head bobbing with heartbeat",
            "Diastolic murmur best heard at left sternal border",
            "Asymptomatic, found on routine exam",
          ],
          labResults: "Echocardiogram: moderate aortic regurgitation, dilated left ventricle",
          correctAnswer: "Aortic Regurgitation",
          options: [
            "Aortic Regurgitation",
            "Aortic Stenosis",
            "Mitral Stenosis",
            "Mitral Regurgitation",
          ],
          explanation:
            "Wide pulse pressure, bounding pulses, and diastolic murmur are classic signs of aortic regurgitation. Head bobbing (de Musset's sign) is a characteristic finding.",
        },
        {
          id: "cardio-easy-16",
          patientInfo: "62-year-old female",
          symptoms: [
            "Fatigue and weakness",
            "Occasional near-syncope",
            "Heart rate varies from 40 to 120 bpm",
            "Palpitations",
            "History of prior atrial fibrillation",
          ],
          labResults: "Holter monitor: alternating bradycardia and tachycardia, sinus pauses",
          correctAnswer: "Sick Sinus Syndrome",
          options: [
            "Sick Sinus Syndrome",
            "Atrial Fibrillation",
            "Complete Heart Block",
            "Sinus Bradycardia",
          ],
          explanation:
            "Alternating bradycardia and tachycardia (tachy-brady syndrome) with sinus pauses is characteristic of sick sinus syndrome.",
        },
        {
          id: "cardio-easy-17",
          patientInfo: "75-year-old male",
          symptoms: [
            "Syncope while shaving",
            "No warning symptoms before fainting",
            "Quick recovery after episode",
            "No chest pain or palpitations",
            "History of hypertension",
          ],
          labResults: "Carotid sinus massage: asystole for 4 seconds",
          correctAnswer: "Carotid Sinus Hypersensitivity",
          options: [
            "Carotid Sinus Hypersensitivity",
            "Vasovagal Syncope",
            "Orthostatic Hypotension",
            "Cardiac Arrhythmia",
          ],
          explanation:
            "Syncope triggered by neck manipulation (shaving) with positive carotid sinus massage test indicates carotid sinus hypersensitivity.",
        },
        {
          id: "cardio-easy-18",
          patientInfo: "30-year-old female",
          symptoms: [
            "Palpitations and anxiety",
            "Heat intolerance",
            "Weight loss despite good appetite",
            "Tremor and sweating",
            "Resting heart rate of 110 bpm",
          ],
          labResults: "TSH: very low, Free T4: elevated, ECG: sinus tachycardia",
          correctAnswer: "Sinus Tachycardia (Hyperthyroidism)",
          options: [
            "Sinus Tachycardia (Hyperthyroidism)",
            "Atrial Fibrillation",
            "Anxiety Disorder",
            "Pheochromocytoma",
          ],
          explanation:
            "Sinus tachycardia with symptoms of hyperthyroidism (heat intolerance, weight loss, tremor) and abnormal thyroid function tests. The cardiac manifestation is secondary to thyroid disease.",
        },
        {
          id: "cardio-easy-19",
          patientInfo: "58-year-old male",
          symptoms: [
            "Sudden severe chest pain",
            "Pain radiates to back",
            "Tearing quality",
            "Unequal blood pressure in arms",
            "History of uncontrolled hypertension",
          ],
          labResults: "CT angiogram: intimal flap in ascending aorta",
          correctAnswer: "Aortic Dissection",
          options: [
            "Aortic Dissection",
            "Myocardial Infarction",
            "Pulmonary Embolism",
            "Pericarditis",
          ],
          explanation:
            "Sudden tearing chest pain radiating to back with unequal arm blood pressures and CT showing intimal flap is diagnostic of aortic dissection.",
        },
        {
          id: "cardio-easy-20",
          patientInfo: "48-year-old male",
          symptoms: [
            "Sudden onset palpitations",
            "Chest tightness",
            "Dizziness",
            "Near syncope",
            "No prior cardiac history",
          ],
          labResults: "ECG: wide complex tachycardia at 200 bpm, AV dissociation present",
          correctAnswer: "Ventricular Tachycardia",
          options: [
            "Ventricular Tachycardia",
            "Supraventricular Tachycardia with Aberrancy",
            "Atrial Fibrillation with WPW",
            "Torsades de Pointes",
          ],
          explanation:
            "Wide complex tachycardia with AV dissociation is highly suggestive of ventricular tachycardia, which requires urgent treatment.",
        },
      ],
      medium: [
        {
          id: "cardio-med-1",
          patientInfo: "45-year-old male",
          symptoms: [
            "Sudden sharp chest pain, worse with deep breathing",
            "Pain radiating to shoulders",
            "Recent viral illness 2 weeks ago",
            "Fever and fatigue",
            "Pericardial friction rub on exam",
          ],
          labResults:
            "ECG: widespread ST elevation, Echocardiogram: pericardial effusion",
          correctAnswer: "Acute Pericarditis",
          options: [
            "Acute Pericarditis",
            "Myocardial Infarction",
            "Pulmonary Embolism",
            "Aortic Dissection",
          ],
          explanation:
            "Post-viral pericarditis presents with pleuritic chest pain, pericardial friction rub, and widespread ST elevation (unlike the localized ST elevation in MI). Recent viral illness is a key clue.",
        },
        {
          id: "cardio-med-2",
          patientInfo: "68-year-old male",
          symptoms: [
            "Syncope (fainting) during exertion",
            "Chest pain with activity",
            "Shortness of breath climbing stairs",
            "Harsh systolic murmur at right upper sternal border",
            "Radiates to carotid arteries",
          ],
          labResults:
            "Echocardiogram: calcified aortic valve, valve area 0.8 cm², pressure gradient 50 mmHg",
          correctAnswer: "Aortic Stenosis",
          options: [
            "Aortic Stenosis",
            "Mitral Regurgitation",
            "Hypertrophic Cardiomyopathy",
            "Pulmonary Stenosis",
          ],
          explanation:
            "Classic triad of syncope, angina, and dyspnea with exertion, along with a harsh systolic ejection murmur radiating to carotids, indicates severe aortic stenosis. Reduced valve area confirms the diagnosis.",
        },
        {
          id: "cardio-med-3",
          patientInfo: "42-year-old female",
          symptoms: [
            "Sudden severe leg swelling (left leg only)",
            "Leg pain and warmth",
            "Recent long flight 3 days ago",
            "On oral contraceptives",
            "No shortness of breath",
          ],
          labResults: "D-dimer: elevated, Doppler ultrasound: thrombus in left femoral vein",
          correctAnswer: "Deep Vein Thrombosis (DVT)",
          options: [
            "Deep Vein Thrombosis (DVT)",
            "Cellulitis",
            "Lymphedema",
            "Baker's Cyst Rupture",
          ],
          explanation:
            "Unilateral leg swelling with pain, warmth, and risk factors (long flight, oral contraceptives) is classic for DVT. Elevated D-dimer and positive ultrasound confirm the diagnosis.",
        },
        {
          id: "cardio-med-4",
          patientInfo: "50-year-old male",
          symptoms: [
            "Sudden severe tearing chest pain",
            "Pain radiates to the back between shoulder blades",
            "History of poorly controlled hypertension",
            "Blood pressure difference between arms (20 mmHg)",
            "New aortic regurgitation murmur",
          ],
          labResults: "CT angiogram: intimal flap in descending aorta",
          correctAnswer: "Aortic Dissection",
          options: [
            "Aortic Dissection",
            "Myocardial Infarction",
            "Pulmonary Embolism",
            "Esophageal Rupture",
          ],
          explanation:
            "Sudden tearing chest pain radiating to the back, blood pressure differential between arms, and CT showing an intimal flap are diagnostic of aortic dissection. Hypertension is the major risk factor.",
        },
        {
          id: "cardio-med-5",
          patientInfo: "58-year-old male",
          symptoms: [
            "Progressive shortness of breath on exertion",
            "Fatigue and reduced exercise tolerance",
            "Orthopnea and paroxysmal nocturnal dyspnea",
            "Leg swelling",
            "History of heavy alcohol use",
          ],
          labResults: "Echocardiogram: dilated left ventricle, ejection fraction 25%",
          correctAnswer: "Dilated Cardiomyopathy",
          options: [
            "Dilated Cardiomyopathy",
            "Hypertrophic Cardiomyopathy",
            "Aortic Stenosis",
            "Pulmonary Hypertension",
          ],
          explanation:
            "Systolic heart failure symptoms with a dilated LV and low ejection fraction suggest dilated cardiomyopathy. Alcohol is a common reversible cause.",
        },
        {
          id: "cardio-med-6",
          patientInfo: "29-year-old male",
          symptoms: [
            "Chest pain after recent viral illness",
            "Shortness of breath",
            "Fatigue and low-grade fever",
            "Palpitations",
            "No relief with antacids",
          ],
          labResults: "Troponin: mildly elevated, ECG: non-specific ST-T changes, Echo: mild LV dysfunction",
          correctAnswer: "Myocarditis",
          options: [
            "Myocarditis",
            "Acute Pericarditis",
            "Myocardial Infarction",
            "Pulmonary Embolism",
          ],
          explanation:
            "Post-viral chest pain with mild troponin rise and LV dysfunction suggests myocarditis rather than ischemia.",
        },
        {
          id: "cardio-med-7",
          patientInfo: "55-year-old female",
          symptoms: [
            "Progressive dyspnea over months",
            "Low-pitched diastolic rumble at apex",
            "Opening snap heard",
            "Atrial fibrillation on exam",
            "History of rheumatic fever",
          ],
          labResults: "Echo: thickened mitral valve leaflets, valve area 1.2 cm², mean gradient 12 mmHg",
          correctAnswer: "Mitral Stenosis",
          options: [
            "Mitral Stenosis",
            "Mitral Regurgitation",
            "Aortic Stenosis",
            "Tricuspid Stenosis",
          ],
          explanation:
            "Low-pitched diastolic rumble with opening snap, reduced valve area, and history of rheumatic fever is classic for mitral stenosis.",
        },
        {
          id: "cardio-med-8",
          patientInfo: "42-year-old male",
          symptoms: [
            "Exertional dyspnea and chest discomfort",
            "Family history of sudden cardiac death",
            "Harsh systolic murmur at left sternal border",
            "Murmur increases with Valsalva",
            "Young athlete",
          ],
          labResults: "Echo: asymmetric septal hypertrophy (18mm), systolic anterior motion of mitral valve",
          correctAnswer: "Hypertrophic Cardiomyopathy",
          options: [
            "Hypertrophic Cardiomyopathy",
            "Aortic Stenosis",
            "Dilated Cardiomyopathy",
            "Athlete's Heart",
          ],
          explanation:
            "Asymmetric septal hypertrophy with SAM, murmur that increases with Valsalva, and family history of sudden death points to hypertrophic cardiomyopathy.",
        },
        {
          id: "cardio-med-9",
          patientInfo: "65-year-old female",
          symptoms: [
            "Progressive shortness of breath",
            "Peripheral edema",
            "Elevated JVP with prominent y descent",
            "Kussmaul sign present",
            "History of tuberculosis years ago",
          ],
          labResults: "Echo: thickened pericardium, septal bounce, CT: pericardial calcification",
          correctAnswer: "Constrictive Pericarditis",
          options: [
            "Constrictive Pericarditis",
            "Restrictive Cardiomyopathy",
            "Cardiac Tamponade",
            "Right Heart Failure",
          ],
          explanation:
            "Kussmaul sign (JVP rise with inspiration), pericardial calcification, and history of TB suggest constrictive pericarditis.",
        },
        {
          id: "cardio-med-10",
          patientInfo: "58-year-old male",
          symptoms: [
            "Progressive fatigue and dyspnea",
            "Elevated JVP",
            "Hepatomegaly",
            "Peripheral edema",
            "Clear lung fields",
          ],
          labResults: "Echo: dilated RV, severe tricuspid regurgitation, normal LV function",
          correctAnswer: "Right Heart Failure",
          options: [
            "Right Heart Failure",
            "Left Heart Failure",
            "Constrictive Pericarditis",
            "Hepatic Cirrhosis",
          ],
          explanation:
            "Signs of systemic venous congestion (elevated JVP, hepatomegaly, edema) with clear lungs and RV dilation indicate isolated right heart failure.",
        },
        {
          id: "cardio-med-11",
          patientInfo: "72-year-old male",
          symptoms: [
            "Syncope with exertion",
            "Narrow pulse pressure",
            "Soft S2",
            "Slow rising carotid pulse",
            "History of bicuspid aortic valve",
          ],
          labResults: "Echo: heavily calcified aortic valve, valve area 0.7 cm², peak gradient 70 mmHg",
          correctAnswer: "Severe Aortic Stenosis",
          options: [
            "Severe Aortic Stenosis",
            "Hypertrophic Cardiomyopathy",
            "Severe Mitral Regurgitation",
            "Pulmonary Hypertension",
          ],
          explanation:
            "Exertional syncope, parvus et tardus pulse, soft S2, and severely reduced valve area indicate critical aortic stenosis requiring intervention.",
        },
        {
          id: "cardio-med-12",
          patientInfo: "38-year-old female",
          symptoms: [
            "Acute dyspnea and pleuritic chest pain",
            "Tachycardia",
            "Recent 10-hour flight",
            "Right leg swelling",
            "Low-grade fever",
          ],
          labResults: "D-dimer: elevated, CT pulmonary angiogram: filling defects in right pulmonary artery",
          correctAnswer: "Pulmonary Embolism",
          options: [
            "Pulmonary Embolism",
            "Pneumonia",
            "Acute Pericarditis",
            "Myocardial Infarction",
          ],
          explanation:
            "Acute dyspnea with pleuritic pain, risk factors (immobility), leg swelling, and CT showing filling defects confirms pulmonary embolism.",
        },
        {
          id: "cardio-med-13",
          patientInfo: "50-year-old male",
          symptoms: [
            "Hypotension",
            "Elevated JVP",
            "Muffled heart sounds",
            "Pulsus paradoxus present",
            "Recent pericarditis",
          ],
          labResults: "Echo: large pericardial effusion with RV diastolic collapse",
          correctAnswer: "Cardiac Tamponade",
          options: [
            "Cardiac Tamponade",
            "Constrictive Pericarditis",
            "Massive PE",
            "Cardiogenic Shock",
          ],
          explanation:
            "Beck's triad (hypotension, elevated JVP, muffled sounds) with pulsus paradoxus and RV collapse on echo confirms cardiac tamponade.",
        },
        {
          id: "cardio-med-14",
          patientInfo: "67-year-old female",
          symptoms: [
            "Progressive dyspnea",
            "Fatigue",
            "Loud P2 on exam",
            "Right ventricular heave",
            "History of sleep apnea and obesity",
          ],
          labResults: "Echo: elevated RVSP 55 mmHg, dilated right heart, normal left heart",
          correctAnswer: "Pulmonary Hypertension",
          options: [
            "Pulmonary Hypertension",
            "Left Heart Failure",
            "COPD",
            "Interstitial Lung Disease",
          ],
          explanation:
            "Loud P2, RV heave, elevated RVSP with normal left heart function suggests pulmonary hypertension, which can be caused by sleep apnea.",
        },
        {
          id: "cardio-med-15",
          patientInfo: "45-year-old male",
          symptoms: [
            "Fever and chills",
            "New murmur",
            "Splinter hemorrhages",
            "Janeway lesions on palms",
            "History of IV drug use",
          ],
          labResults: "Blood cultures: Staphylococcus aureus, Echo: vegetation on tricuspid valve",
          correctAnswer: "Infective Endocarditis",
          options: [
            "Infective Endocarditis",
            "Rheumatic Fever",
            "Sepsis without Endocarditis",
            "Systemic Lupus Erythematosus",
          ],
          explanation:
            "Fever, new murmur, peripheral stigmata (splinter hemorrhages, Janeway lesions), positive blood cultures, and vegetation confirm infective endocarditis.",
        },
        {
          id: "cardio-med-16",
          patientInfo: "70-year-old male",
          symptoms: [
            "Lightheadedness and fatigue",
            "Heart rate of 35 bpm",
            "No response to atropine",
            "Cannon A waves in JVP",
            "History of anterior MI",
          ],
          labResults: "ECG: P waves and QRS complexes with no relationship, ventricular rate 35 bpm",
          correctAnswer: "Complete Heart Block",
          options: [
            "Complete Heart Block",
            "Second Degree AV Block Type II",
            "Sick Sinus Syndrome",
            "Sinus Bradycardia",
          ],
          explanation:
            "Complete AV dissociation with slow ventricular rate and cannon A waves indicates complete (third degree) heart block, often post-MI.",
        },
        {
          id: "cardio-med-17",
          patientInfo: "55-year-old female",
          symptoms: [
            "Palpitations",
            "Pre-syncope",
            "ECG showing short PR interval",
            "Delta waves present",
            "Episodes of rapid regular tachycardia",
          ],
          labResults: "ECG: PR interval 100ms, slurred QRS upstroke (delta wave), QRS slightly widened",
          correctAnswer: "Wolff-Parkinson-White Syndrome",
          options: [
            "Wolff-Parkinson-White Syndrome",
            "AV Nodal Reentrant Tachycardia",
            "Atrial Flutter",
            "Bundle Branch Block",
          ],
          explanation:
            "Short PR interval with delta waves (slurred QRS upstroke) is pathognomonic for WPW syndrome, caused by an accessory pathway.",
        },
        {
          id: "cardio-med-18",
          patientInfo: "62-year-old male",
          symptoms: [
            "Sudden collapse during exercise",
            "Brief loss of consciousness",
            "Quick recovery",
            "No post-ictal confusion",
            "History of coronary artery disease",
          ],
          labResults: "Event monitor: episode of sustained VT at 220 bpm, Echo: EF 35%",
          correctAnswer: "Ventricular Tachycardia with Syncope",
          options: [
            "Ventricular Tachycardia with Syncope",
            "Vasovagal Syncope",
            "Aortic Stenosis",
            "Seizure",
          ],
          explanation:
            "Exertional syncope with documented VT and reduced EF in a CAD patient indicates malignant arrhythmia requiring ICD evaluation.",
        },
        {
          id: "cardio-med-19",
          patientInfo: "48-year-old male",
          symptoms: [
            "Progressive dyspnea on exertion",
            "Fatigue",
            "Dilated cardiomyopathy on echo",
            "S3 gallop",
            "Heavy alcohol use for 20 years",
          ],
          labResults: "Echo: EF 25%, all chambers dilated, no significant valvular disease",
          correctAnswer: "Alcoholic Cardiomyopathy",
          options: [
            "Alcoholic Cardiomyopathy",
            "Ischemic Cardiomyopathy",
            "Viral Cardiomyopathy",
            "Peripartum Cardiomyopathy",
          ],
          explanation:
            "Dilated cardiomyopathy with reduced EF in setting of chronic heavy alcohol use suggests alcoholic cardiomyopathy.",
        },
        {
          id: "cardio-med-20",
          patientInfo: "75-year-old female",
          symptoms: [
            "Syncope",
            "Progressive heart block on serial ECGs",
            "Fatigue",
            "PR interval progressively lengthening",
            "Dropped beats",
          ],
          labResults: "ECG: progressive PR prolongation until QRS drops, then cycle repeats",
          correctAnswer: "Second Degree AV Block Type I (Wenckebach)",
          options: [
            "Second Degree AV Block Type I (Wenckebach)",
            "Second Degree AV Block Type II",
            "First Degree AV Block",
            "Complete Heart Block",
          ],
          explanation:
            "Progressive PR prolongation until a beat is dropped (Wenckebach pattern) is characteristic of Mobitz Type I second degree AV block.",
        },
      ],
      hard: [
        {
          id: "cardio-hard-1",
          patientInfo: "35-year-old female",
          symptoms: [
            "Intermittent palpitations and lightheadedness",
            "Episodes of rapid heartbeat (200 bpm)",
            "Sudden onset and termination",
            "No chest pain",
            "Otherwise healthy, no cardiac history",
          ],
          labResults:
            "ECG during episode: narrow complex tachycardia, P waves hidden in QRS",
          correctAnswer: "Supraventricular Tachycardia (SVT)",
          options: [
            "Supraventricular Tachycardia (SVT)",
            "Ventricular Tachycardia",
            "Atrial Fibrillation",
            "Sinus Tachycardia",
          ],
          explanation:
            "Young patient with paroxysmal narrow complex tachycardia with sudden onset/termination is classic for SVT. The lack of visible P waves suggests AV nodal reentry tachycardia (AVNRT), the most common type of SVT.",
        },
        {
          id: "cardio-hard-2",
          patientInfo: "28-year-old male athlete",
          symptoms: [
            "Syncope during basketball game",
            "Family history of sudden cardiac death (uncle at age 30)",
            "Previous episodes of exertional chest discomfort",
            "Palpitations with exercise",
            "Harsh systolic murmur that increases with Valsalva",
          ],
          labResults:
            "ECG: left ventricular hypertrophy, deep Q waves in lateral leads, Echocardiogram: asymmetric septal hypertrophy (18mm)",
          correctAnswer: "Hypertrophic Cardiomyopathy (HCM)",
          options: [
            "Hypertrophic Cardiomyopathy (HCM)",
            "Aortic Stenosis",
            "Athlete's Heart",
            "Dilated Cardiomyopathy",
          ],
          explanation:
            "Young athlete with exertional syncope, family history of sudden death, and asymmetric septal hypertrophy is classic for HCM. The murmur increasing with Valsalva (decreased preload) is characteristic.",
        },
        {
          id: "cardio-hard-3",
          patientInfo: "60-year-old female",
          symptoms: [
            "Progressive dyspnea over 6 months",
            "Fatigue and decreased exercise tolerance",
            "Loud P2 heart sound on exam",
            "Lower extremity edema",
            "History of sleep apnea and obesity",
          ],
          labResults:
            "Echocardiogram: right ventricular dilation, tricuspid regurgitation, Right heart catheterization: mean PA pressure 45 mmHg",
          correctAnswer: "Pulmonary Hypertension",
          options: [
            "Pulmonary Hypertension",
            "Left Heart Failure",
            "Chronic Obstructive Pulmonary Disease",
            "Pulmonary Embolism",
          ],
          explanation:
            "Progressive dyspnea with right heart findings (RV dilation, loud P2, peripheral edema) and elevated pulmonary artery pressure on catheterization confirms pulmonary hypertension. Sleep apnea is a known cause.",
        },
        {
          id: "cardio-hard-4",
          patientInfo: "75-year-old male",
          symptoms: [
            "Dizziness and near-syncope",
            "Fatigue and exercise intolerance",
            "Heart rate of 35 bpm",
            "No chest pain",
            "History of prior inferior MI",
          ],
          labResults:
            "ECG: complete dissociation between P waves and QRS complexes, ventricular rate 35 bpm, atrial rate 80 bpm",
          correctAnswer: "Third-Degree (Complete) Heart Block",
          options: [
            "Third-Degree (Complete) Heart Block",
            "Sick Sinus Syndrome",
            "Second-Degree Heart Block Type I",
            "Atrial Fibrillation with Slow Ventricular Response",
          ],
          explanation:
            "Complete AV dissociation with independent atrial and ventricular rates is diagnostic of third-degree heart block. Prior inferior MI can damage the AV node. This condition requires pacemaker implantation.",
        },
        {
          id: "cardio-hard-5",
          patientInfo: "41-year-old male",
          symptoms: [
            "Fever and chills for 1 week",
            "New heart murmur",
            "Night sweats and fatigue",
            "Tender nodules on fingertips",
            "History of IV drug use",
          ],
          labResults: "Blood cultures: positive for Staphylococcus aureus, echocardiogram: tricuspid valve vegetation",
          correctAnswer: "Infective Endocarditis",
          options: [
            "Infective Endocarditis",
            "Acute Pericarditis",
            "Rheumatic Fever",
            "Myocarditis",
          ],
          explanation:
            "Fever, new murmur, positive blood cultures, and valve vegetation in an IV drug user indicate infective endocarditis, often involving the tricuspid valve.",
        },
        {
          id: "cardio-hard-6",
          patientInfo: "56-year-old female",
          symptoms: [
            "Severe shortness of breath",
            "Chest discomfort",
            "Lightheadedness",
            "Muffled heart sounds",
            "Hypotension with elevated JVP",
          ],
          labResults: "Echo: large pericardial effusion with right atrial collapse",
          correctAnswer: "Cardiac Tamponade",
          options: [
            "Cardiac Tamponade",
            "Acute Pericarditis",
            "Heart Failure",
            "Pulmonary Embolism",
          ],
          explanation:
            "Beck triad (hypotension, JVP elevation, muffled heart sounds) with echo showing chamber collapse is diagnostic of cardiac tamponade.",
        },
        {
          id: "cardio-hard-7",
          patientInfo: "28-year-old female",
          symptoms: [
            "Dyspnea 2 weeks postpartum",
            "Orthopnea",
            "Lower extremity edema",
            "No prior cardiac history",
            "Uncomplicated pregnancy until now",
          ],
          labResults: "Echo: EF 30%, dilated LV, BNP: markedly elevated",
          correctAnswer: "Peripartum Cardiomyopathy",
          options: [
            "Peripartum Cardiomyopathy",
            "Pulmonary Embolism",
            "Preeclampsia",
            "Amniotic Fluid Embolism",
          ],
          explanation:
            "New heart failure in the last month of pregnancy or within 5 months postpartum without other identifiable cause defines peripartum cardiomyopathy.",
        },
        {
          id: "cardio-hard-8",
          patientInfo: "65-year-old male",
          symptoms: [
            "Chest pain after emotional stress",
            "ST elevation on ECG",
            "Normal coronary arteries on angiogram",
            "Apical ballooning on ventriculogram",
            "Recent death of spouse",
          ],
          labResults: "Troponin: mildly elevated, Coronary angiogram: no significant CAD, Echo: apical akinesis",
          correctAnswer: "Takotsubo Cardiomyopathy",
          options: [
            "Takotsubo Cardiomyopathy",
            "STEMI",
            "Myocarditis",
            "Coronary Vasospasm",
          ],
          explanation:
            "Stress-induced cardiomyopathy (Takotsubo) presents like ACS but with clean coronaries and characteristic apical ballooning pattern.",
        },
        {
          id: "cardio-hard-9",
          patientInfo: "52-year-old male",
          symptoms: [
            "Exertional chest pain",
            "Normal resting ECG",
            "Pain occurs at same level of exertion",
            "Relieved by sublingual nitrate",
            "Known history of CAD",
          ],
          labResults: "Stress test: 2mm ST depression at peak exercise, normal at rest, Troponin: negative",
          correctAnswer: "Stable Angina with Significant CAD",
          options: [
            "Stable Angina with Significant CAD",
            "Unstable Angina",
            "Prinzmetal Angina",
            "Microvascular Angina",
          ],
          explanation:
            "Predictable exertional angina with positive stress test and negative troponin indicates stable angina from fixed coronary lesions.",
        },
        {
          id: "cardio-hard-10",
          patientInfo: "45-year-old female",
          symptoms: [
            "Chest pain at rest, often at night",
            "ST elevation during episodes",
            "Normal ECG between episodes",
            "Pain relieved by calcium channel blockers",
            "Non-smoker, no traditional risk factors",
          ],
          labResults: "Coronary angiogram: normal coronaries, Acetylcholine provocation: coronary spasm",
          correctAnswer: "Prinzmetal (Variant) Angina",
          options: [
            "Prinzmetal (Variant) Angina",
            "Unstable Angina",
            "STEMI",
            "Costochondritis",
          ],
          explanation:
            "Rest angina with transient ST elevation, normal coronaries, and positive provocation test confirms vasospastic (Prinzmetal) angina.",
        },
        {
          id: "cardio-hard-11",
          patientInfo: "70-year-old female",
          symptoms: [
            "Heart failure symptoms",
            "Thickened ventricular walls on echo",
            "Sparkling myocardium appearance",
            "Low voltage on ECG despite thick walls",
            "Peripheral neuropathy",
          ],
          labResults: "Echo: increased wall thickness, diastolic dysfunction, Serum/urine protein electrophoresis: monoclonal protein",
          correctAnswer: "Cardiac Amyloidosis",
          options: [
            "Cardiac Amyloidosis",
            "Hypertrophic Cardiomyopathy",
            "Hypertensive Heart Disease",
            "Fabry Disease",
          ],
          explanation:
            "Low voltage-mass mismatch (thick walls but low ECG voltage), sparkling myocardium, and systemic features suggest cardiac amyloidosis.",
        },
        {
          id: "cardio-hard-12",
          patientInfo: "35-year-old male",
          symptoms: [
            "Syncope during exercise",
            "Family history of sudden death",
            "Palpitations",
            "T wave inversions V1-V3",
            "Epsilon waves on ECG",
          ],
          labResults: "MRI: fatty infiltration of RV, RV dilation, Echo: RV dysfunction",
          correctAnswer: "Arrhythmogenic RV Cardiomyopathy",
          options: [
            "Arrhythmogenic RV Cardiomyopathy",
            "Brugada Syndrome",
            "Pulmonary Hypertension",
            "Hypertrophic Cardiomyopathy",
          ],
          explanation:
            "Epsilon waves, T inversions in right precordial leads, RV fatty replacement, and family history are diagnostic of ARVC.",
        },
        {
          id: "cardio-hard-13",
          patientInfo: "40-year-old male",
          symptoms: [
            "Syncope at rest",
            "Family history of sudden cardiac death",
            "Coved ST elevation V1-V2",
            "Type 1 Brugada pattern on ECG",
            "Episodes occur at night",
          ],
          labResults: "ECG: coved ST elevation >2mm in V1-V2 with negative T wave, Genetic testing: SCN5A mutation",
          correctAnswer: "Brugada Syndrome",
          options: [
            "Brugada Syndrome",
            "STEMI",
            "ARVC",
            "Long QT Syndrome",
          ],
          explanation:
            "Type 1 Brugada pattern (coved ST elevation with negative T wave in V1-V2), syncope, and SCN5A mutation confirm Brugada syndrome.",
        },
        {
          id: "cardio-hard-14",
          patientInfo: "25-year-old female",
          symptoms: [
            "Syncope with emotional stress",
            "QTc of 520ms on ECG",
            "Family history of drowning death",
            "Torsades de pointes on monitor",
            "Currently on clarithromycin",
          ],
          labResults: "ECG: prolonged QTc 520ms, Genetic testing: KCNQ1 mutation",
          correctAnswer: "Long QT Syndrome",
          options: [
            "Long QT Syndrome",
            "Brugada Syndrome",
            "Catecholaminergic Polymorphic VT",
            "Short QT Syndrome",
          ],
          explanation:
            "Prolonged QTc >500ms with Torsades, syncope triggers, and KCNQ1 mutation indicate congenital long QT syndrome (type 1).",
        },
        {
          id: "cardio-hard-15",
          patientInfo: "18-year-old male",
          symptoms: [
            "Syncope during swimming",
            "Polymorphic VT during exercise stress test",
            "Normal resting ECG",
            "Normal QT interval",
            "Family history of sudden death during exercise",
          ],
          labResults: "Exercise test: bidirectional VT at peak exercise, Genetic testing: RYR2 mutation",
          correctAnswer: "Catecholaminergic Polymorphic VT",
          options: [
            "Catecholaminergic Polymorphic VT",
            "Long QT Syndrome",
            "Brugada Syndrome",
            "WPW Syndrome",
          ],
          explanation:
            "Exercise-induced polymorphic/bidirectional VT with normal resting ECG and RYR2 mutation is diagnostic of CPVT.",
        },
        {
          id: "cardio-hard-16",
          patientInfo: "60-year-old male",
          symptoms: [
            "Bilateral arm claudication",
            "Blood pressure difference >20mmHg between arms",
            "Carotid bruit",
            "Visual disturbances",
            "Elevated ESR",
          ],
          labResults: "CTA: thickening of aortic wall, stenosis of subclavian arteries, ESR: 85mm/hr",
          correctAnswer: "Takayasu Arteritis",
          options: [
            "Takayasu Arteritis",
            "Giant Cell Arteritis",
            "Atherosclerotic Disease",
            "Aortic Dissection",
          ],
          explanation:
            "Large vessel vasculitis with arm claudication, pulse deficits, and aortic involvement in younger patient suggests Takayasu arteritis.",
        },
        {
          id: "cardio-hard-17",
          patientInfo: "72-year-old female",
          symptoms: [
            "New severe headache",
            "Jaw claudication",
            "Visual loss in one eye",
            "Scalp tenderness",
            "Elevated inflammatory markers",
          ],
          labResults: "ESR: 95mm/hr, CRP: elevated, Temporal artery biopsy: giant cell infiltration",
          correctAnswer: "Giant Cell Arteritis",
          options: [
            "Giant Cell Arteritis",
            "Takayasu Arteritis",
            "Migraine",
            "Cluster Headache",
          ],
          explanation:
            "New headache in elderly with jaw claudication, visual symptoms, elevated ESR, and positive temporal artery biopsy confirms GCA.",
        },
        {
          id: "cardio-hard-18",
          patientInfo: "55-year-old male",
          symptoms: [
            "Severe tearing back pain",
            "Hypertension",
            "New aortic regurgitation murmur",
            "Pulse deficit in left arm",
            "Widened mediastinum on CXR",
          ],
          labResults: "CT angiogram: dissection flap from ascending aorta extending to iliac arteries",
          correctAnswer: "Type A Aortic Dissection",
          options: [
            "Type A Aortic Dissection",
            "Type B Aortic Dissection",
            "Aortic Aneurysm Rupture",
            "Myocardial Infarction",
          ],
          explanation:
            "Dissection involving ascending aorta (Type A) is a surgical emergency. New AR murmur and pulse deficits indicate extension.",
        },
        {
          id: "cardio-hard-19",
          patientInfo: "48-year-old male",
          symptoms: [
            "Recurrent DVTs",
            "Arterial thrombosis",
            "Livedo reticularis",
            "History of stroke at age 40",
            "Recurrent pregnancy losses (spouse)",
          ],
          labResults: "Lupus anticoagulant: positive, Anticardiolipin antibodies: high titer, Beta-2 glycoprotein I: positive",
          correctAnswer: "Antiphospholipid Syndrome",
          options: [
            "Antiphospholipid Syndrome",
            "Factor V Leiden",
            "Protein C Deficiency",
            "Heparin-Induced Thrombocytopenia",
          ],
          explanation:
            "Recurrent arterial and venous thrombosis with positive antiphospholipid antibodies on two occasions defines antiphospholipid syndrome.",
        },
        {
          id: "cardio-hard-20",
          patientInfo: "62-year-old female",
          symptoms: [
            "Heart failure",
            "Restrictive filling pattern",
            "Low voltage ECG",
            "Thickened heart walls",
            "Carpal tunnel syndrome bilaterally",
          ],
          labResults: "Echo: grade 2 diastolic dysfunction, wall thickness 14mm, Technetium pyrophosphate scan: grade 3 uptake",
          correctAnswer: "Transthyretin Cardiac Amyloidosis",
          options: [
            "Transthyretin Cardiac Amyloidosis",
            "AL Amyloidosis",
            "Hypertrophic Cardiomyopathy",
            "Sarcoidosis",
          ],
          explanation:
            "Strong pyrophosphate uptake without light chain involvement suggests ATTR amyloidosis. Bilateral carpal tunnel is a classic early sign.",
        },
      ],
    },
    neurology: {
      easy: [
        {
          id: "neuro-easy-1",
          patientInfo: "70-year-old female",
          symptoms: [
            "Progressive memory loss over 2 years",
            "Difficulty with familiar tasks",
            "Confusion about time and place",
            "Misplacing items frequently",
            "Changes in mood and personality",
          ],
          labResults: "MRI: cortical atrophy, hippocampal shrinkage",
          correctAnswer: "Alzheimer's Disease",
          options: [
            "Alzheimer's Disease",
            "Normal Aging",
            "Vascular Dementia",
            "Depression",
          ],
          explanation:
            "Progressive cognitive decline with memory loss, confusion, and behavioral changes, along with characteristic MRI findings of cortical atrophy and hippocampal shrinkage, are consistent with Alzheimer's Disease.",
        },
        {
          id: "neuro-easy-2",
          patientInfo: "68-year-old male",
          symptoms: [
            "Sudden right-sided weakness",
            "Slurred speech",
            "Facial droop on right side",
            "Symptoms started 2 hours ago",
            "History of atrial fibrillation",
          ],
          labResults: "CT head: no hemorrhage, CT angiogram: left MCA occlusion",
          correctAnswer: "Ischemic Stroke",
          options: [
            "Ischemic Stroke",
            "Hemorrhagic Stroke",
            "Transient Ischemic Attack",
            "Bell's Palsy",
          ],
          explanation:
            "Sudden onset of unilateral weakness, facial droop, and speech difficulty (classic stroke symptoms) with CT showing vessel occlusion and no hemorrhage indicates ischemic stroke. Atrial fibrillation is a major risk factor.",
        },
        {
          id: "neuro-easy-3",
          patientInfo: "35-year-old female",
          symptoms: [
            "Severe throbbing headache on one side",
            "Nausea and vomiting",
            "Sensitivity to light and sound",
            "Sees flashing lights before headache",
            "Headaches last 4-72 hours",
          ],
          labResults: "Neurological exam: normal, CT head: normal",
          correctAnswer: "Migraine with Aura",
          options: [
            "Migraine with Aura",
            "Tension Headache",
            "Cluster Headache",
            "Subarachnoid Hemorrhage",
          ],
          explanation:
            "Unilateral throbbing headache with nausea, photophobia, phonophobia, and preceding visual symptoms (aura) is classic for migraine with aura. Normal imaging rules out structural causes.",
        },
        {
          id: "neuro-easy-4",
          patientInfo: "62-year-old male",
          symptoms: [
            "Resting tremor in right hand",
            "Slow movements (bradykinesia)",
            "Muscle stiffness",
            "Shuffling gait",
            "Reduced facial expression",
          ],
          labResults: "Clinical diagnosis, DaTscan: reduced dopamine uptake in striatum",
          correctAnswer: "Parkinson's Disease",
          options: [
            "Parkinson's Disease",
            "Essential Tremor",
            "Drug-Induced Parkinsonism",
            "Normal Pressure Hydrocephalus",
          ],
          explanation:
            "Classic tetrad of resting tremor, bradykinesia, rigidity, and postural instability with reduced facial expression (masked facies) is diagnostic of Parkinson's disease. DaTscan confirms dopaminergic deficit.",
        },
        {
          id: "neuro-easy-5",
          patientInfo: "40-year-old male",
          symptoms: [
            "Bilateral, band-like headache",
            "Pressure sensation around the head",
            "No nausea or vomiting",
            "Worse after stress at work",
            "Relief with rest",
          ],
          labResults: "Neurologic exam: normal",
          correctAnswer: "Tension-Type Headache",
          options: [
            "Tension-Type Headache",
            "Migraine with Aura",
            "Cluster Headache",
            "Subarachnoid Hemorrhage",
          ],
          explanation:
            "Tension-type headaches are bilateral, pressure-like, and not associated with nausea or focal deficits. Normal exam supports the diagnosis.",
        },
        {
          id: "neuro-easy-6",
          patientInfo: "38-year-old office worker",
          symptoms: [
            "Numbness and tingling in thumb and index finger",
            "Symptoms worse at night",
            "Hand weakness with gripping",
            "Relief when shaking the hand",
            "Repetitive keyboard use",
          ],
          labResults: "Tinel sign: positive at wrist, Phalen test: reproduces symptoms",
          correctAnswer: "Carpal Tunnel Syndrome",
          options: [
            "Carpal Tunnel Syndrome",
            "Ulnar Neuropathy",
            "Cervical Radiculopathy",
            "Peripheral Neuropathy",
          ],
          explanation:
            "Median nerve compression causes numbness in thumb/index fingers, nocturnal symptoms, and positive Tinel/Phalen tests.",
        },
        {
          id: "neuro-easy-7",
          patientInfo: "65-year-old male",
          symptoms: [
            "Tremor at rest in right hand",
            "Slowness of movement",
            "Shuffling gait",
            "Reduced arm swing when walking",
            "Masked facial expression",
          ],
          labResults: "Clinical diagnosis, DaTscan: reduced dopamine transporter uptake",
          correctAnswer: "Parkinson's Disease",
          options: [
            "Parkinson's Disease",
            "Essential Tremor",
            "Drug-Induced Parkinsonism",
            "Progressive Supranuclear Palsy",
          ],
          explanation:
            "Classic triad of resting tremor, bradykinesia, and rigidity with unilateral onset is characteristic of Parkinson's disease.",
        },
        {
          id: "neuro-easy-8",
          patientInfo: "25-year-old female",
          symptoms: [
            "Severe throbbing headache on one side",
            "Nausea and vomiting",
            "Sensitivity to light and sound",
            "Visual aura before headache",
            "Headaches last 4-72 hours",
          ],
          labResults: "Neurological exam: normal, CT head: normal",
          correctAnswer: "Migraine with Aura",
          options: [
            "Migraine with Aura",
            "Tension Headache",
            "Cluster Headache",
            "Intracranial Mass",
          ],
          explanation:
            "Unilateral throbbing headache with nausea, photophobia, phonophobia, and preceding visual aura is classic migraine with aura.",
        },
        {
          id: "neuro-easy-9",
          patientInfo: "45-year-old male",
          symptoms: [
            "Excruciating pain around right eye",
            "Eye watering and redness",
            "Nasal congestion on same side",
            "Attacks last 30-90 minutes",
            "Occur at same time daily for weeks",
          ],
          labResults: "Neurological exam: ptosis and miosis during attack",
          correctAnswer: "Cluster Headache",
          options: [
            "Cluster Headache",
            "Migraine",
            "Trigeminal Neuralgia",
            "Temporal Arteritis",
          ],
          explanation:
            "Severe unilateral periorbital pain with autonomic features (lacrimation, rhinorrhea) occurring in clusters is typical of cluster headache.",
        },
        {
          id: "neuro-easy-10",
          patientInfo: "55-year-old female",
          symptoms: [
            "Electric shock-like pain in cheek",
            "Triggered by chewing or touching face",
            "Pain lasts seconds",
            "Multiple attacks per day",
            "No pain between attacks",
          ],
          labResults: "MRI brain: vascular loop compressing trigeminal nerve",
          correctAnswer: "Trigeminal Neuralgia",
          options: [
            "Trigeminal Neuralgia",
            "Cluster Headache",
            "Dental Abscess",
            "TMJ Disorder",
          ],
          explanation:
            "Brief, severe, lancinating facial pain triggered by light touch in trigeminal distribution is pathognomonic for trigeminal neuralgia.",
        },
        {
          id: "neuro-easy-11",
          patientInfo: "30-year-old male",
          symptoms: [
            "Witnessed seizure with loss of consciousness",
            "Whole body shaking",
            "Tongue biting",
            "Urinary incontinence",
            "Confusion after event",
          ],
          labResults: "EEG: generalized spike-wave discharges, MRI brain: normal",
          correctAnswer: "Generalized Tonic-Clonic Seizure",
          options: [
            "Generalized Tonic-Clonic Seizure",
            "Syncope",
            "Psychogenic Non-Epileptic Seizure",
            "Focal Seizure",
          ],
          explanation:
            "Loss of consciousness with tonic-clonic movements, tongue biting, incontinence, and post-ictal confusion indicates generalized tonic-clonic seizure.",
        },
        {
          id: "neuro-easy-12",
          patientInfo: "72-year-old female",
          symptoms: [
            "Sudden right-sided weakness",
            "Slurred speech",
            "Facial droop on right",
            "Symptoms started 2 hours ago",
            "History of atrial fibrillation",
          ],
          labResults: "CT head: no hemorrhage, CT angiogram: left MCA occlusion",
          correctAnswer: "Acute Ischemic Stroke",
          options: [
            "Acute Ischemic Stroke",
            "Hemorrhagic Stroke",
            "Bell's Palsy",
            "Transient Ischemic Attack",
          ],
          explanation:
            "Acute onset of contralateral weakness and speech difficulty with MCA occlusion on imaging confirms ischemic stroke.",
        },
        {
          id: "neuro-easy-13",
          patientInfo: "58-year-old male",
          symptoms: [
            "Sudden severe headache",
            "Worst headache of my life",
            "Neck stiffness",
            "Photophobia",
            "Brief loss of consciousness at onset",
          ],
          labResults: "CT head: blood in subarachnoid space, CT angiogram: aneurysm at anterior communicating artery",
          correctAnswer: "Subarachnoid Hemorrhage",
          options: [
            "Subarachnoid Hemorrhage",
            "Migraine",
            "Meningitis",
            "Tension Headache",
          ],
          explanation:
            "Thunderclap headache (worst ever, sudden onset) with meningismus and blood on CT indicates subarachnoid hemorrhage.",
        },
        {
          id: "neuro-easy-14",
          patientInfo: "68-year-old male",
          symptoms: [
            "Weakness in both legs progressive over months",
            "Difficulty walking",
            "Urinary urgency",
            "Numbness in feet",
            "Back pain",
          ],
          labResults: "MRI spine: cervical cord compression from degenerative disease",
          correctAnswer: "Cervical Myelopathy",
          options: [
            "Cervical Myelopathy",
            "Peripheral Neuropathy",
            "Lumbar Stenosis",
            "ALS",
          ],
          explanation:
            "Progressive leg weakness with upper motor neuron signs, bladder symptoms, and cervical cord compression indicates cervical myelopathy.",
        },
        {
          id: "neuro-easy-15",
          patientInfo: "42-year-old female",
          symptoms: [
            "Double vision",
            "Drooping eyelids worse in evening",
            "Difficulty chewing",
            "Weakness improves with rest",
            "No sensory symptoms",
          ],
          labResults: "Acetylcholine receptor antibodies: positive, EMG: decremental response on repetitive stimulation",
          correctAnswer: "Myasthenia Gravis",
          options: [
            "Myasthenia Gravis",
            "Multiple Sclerosis",
            "Botulism",
            "Lambert-Eaton Syndrome",
          ],
          explanation:
            "Fatigable weakness affecting ocular and bulbar muscles, positive AChR antibodies, and decremental EMG response confirm myasthenia gravis.",
        },
        {
          id: "neuro-easy-16",
          patientInfo: "35-year-old male",
          symptoms: [
            "Ascending weakness starting in legs",
            "Numbness and tingling in feet",
            "Difficulty walking up stairs",
            "Started 2 weeks after viral illness",
            "Areflexia on exam",
          ],
          labResults: "CSF: elevated protein, normal cell count (albuminocytologic dissociation)",
          correctAnswer: "Guillain-Barré Syndrome",
          options: [
            "Guillain-Barré Syndrome",
            "Transverse Myelitis",
            "Peripheral Neuropathy",
            "Myasthenia Gravis",
          ],
          explanation:
            "Post-infectious ascending weakness with areflexia and CSF showing elevated protein without pleocytosis is classic GBS.",
        },
        {
          id: "neuro-easy-17",
          patientInfo: "50-year-old female",
          symptoms: [
            "Bilateral hand tremor",
            "Tremor worse with movement",
            "Improves with alcohol",
            "Family history of tremor",
            "No resting tremor",
          ],
          labResults: "Neurological exam: action tremor, normal gait",
          correctAnswer: "Essential Tremor",
          options: [
            "Essential Tremor",
            "Parkinson's Disease",
            "Cerebellar Tremor",
            "Physiological Tremor",
          ],
          explanation:
            "Bilateral action tremor that improves with alcohol and has positive family history is characteristic of essential tremor.",
        },
        {
          id: "neuro-easy-18",
          patientInfo: "60-year-old male",
          symptoms: [
            "Weakness of right foot",
            "Foot drop",
            "Numbness on top of foot",
            "Difficulty walking",
            "Recent prolonged leg crossing",
          ],
          labResults: "EMG/NCS: common peroneal neuropathy at fibular head",
          correctAnswer: "Peroneal Nerve Palsy",
          options: [
            "Peroneal Nerve Palsy",
            "L5 Radiculopathy",
            "Sciatic Neuropathy",
            "Motor Neuron Disease",
          ],
          explanation:
            "Foot drop with numbness in peroneal distribution following compression (leg crossing) indicates peroneal nerve palsy.",
        },
        {
          id: "neuro-easy-19",
          patientInfo: "28-year-old male",
          symptoms: [
            "Severe lower back pain",
            "Pain radiating down left leg",
            "Worse with coughing or sneezing",
            "Numbness in outer foot",
            "Positive straight leg raise",
          ],
          labResults: "MRI lumbar spine: L5-S1 disc herniation with S1 nerve root compression",
          correctAnswer: "Lumbar Radiculopathy (Sciatica)",
          options: [
            "Lumbar Radiculopathy (Sciatica)",
            "Piriformis Syndrome",
            "Lumbar Stenosis",
            "Sacroiliac Joint Dysfunction",
          ],
          explanation:
            "Radiating leg pain with positive straight leg raise and MRI showing disc herniation confirms lumbar radiculopathy.",
        },
        {
          id: "neuro-easy-20",
          patientInfo: "70-year-old male",
          symptoms: [
            "Memory problems",
            "Urinary incontinence",
            "Gait disturbance (magnetic gait)",
            "Symptoms developed over months",
            "No headache",
          ],
          labResults: "CT head: enlarged ventricles out of proportion to atrophy",
          correctAnswer: "Normal Pressure Hydrocephalus",
          options: [
            "Normal Pressure Hydrocephalus",
            "Alzheimer's Disease",
            "Parkinson's Disease",
            "Vascular Dementia",
          ],
          explanation:
            "Classic triad of dementia, incontinence, and gait disturbance (wet, wacky, wobbly) with ventriculomegaly suggests NPH.",
        },
      ],
      medium: [
        {
          id: "neuro-med-1",
          patientInfo: "28-year-old female",
          symptoms: [
            "Episodes of vision loss in one eye",
            "Weakness and numbness in limbs",
            "Symptoms come and go over months",
            "Fatigue and balance problems",
            "Worsening with heat exposure",
          ],
          labResults:
            "MRI: multiple white matter lesions, CSF: oligoclonal bands",
          correctAnswer: "Multiple Sclerosis",
          options: ["Multiple Sclerosis", "Stroke", "Brain Tumor", "Migraine"],
          explanation:
            "Young patient with relapsing-remitting neurological symptoms, MRI showing demyelinating lesions, and CSF oligoclonal bands are diagnostic of Multiple Sclerosis. Uhthoff's phenomenon (worsening with heat) is characteristic.",
        },
        {
          id: "neuro-med-2",
          patientInfo: "45-year-old female",
          symptoms: [
            "Drooping eyelids worse in the evening",
            "Double vision",
            "Difficulty chewing and swallowing",
            "Weakness improves after rest",
            "Voice becomes nasal after talking",
          ],
          labResults: "Acetylcholine receptor antibodies: positive, EMG: decremental response on repetitive stimulation",
          correctAnswer: "Myasthenia Gravis",
          options: [
            "Myasthenia Gravis",
            "Lambert-Eaton Syndrome",
            "Botulism",
            "Muscular Dystrophy",
          ],
          explanation:
            "Fatigable weakness affecting ocular and bulbar muscles that worsens with activity and improves with rest, positive acetylcholine receptor antibodies, and decremental EMG response are diagnostic of myasthenia gravis.",
        },
        {
          id: "neuro-med-3",
          patientInfo: "22-year-old male",
          symptoms: [
            "Witnessed seizure with convulsions",
            "Loss of consciousness during episode",
            "Tongue biting noted",
            "Urinary incontinence",
            "Confused and sleepy after episode",
          ],
          labResults: "EEG: generalized spike-and-wave discharges, MRI brain: normal",
          correctAnswer: "Generalized Tonic-Clonic Seizure (Epilepsy)",
          options: [
            "Generalized Tonic-Clonic Seizure (Epilepsy)",
            "Syncope",
            "Psychogenic Non-Epileptic Seizure",
            "Hypoglycemic Episode",
          ],
          explanation:
            "Witnessed convulsive episode with loss of consciousness, tongue biting, incontinence, and postictal confusion is classic for generalized tonic-clonic seizure. EEG findings confirm epilepsy.",
        },
        {
          id: "neuro-med-4",
          patientInfo: "50-year-old male",
          symptoms: [
            "Severe sudden headache ('worst of my life')",
            "Neck stiffness",
            "Vomiting",
            "Brief loss of consciousness at onset",
            "Photophobia",
          ],
          labResults: "CT head: hyperdense material in subarachnoid space, LP: xanthochromia",
          correctAnswer: "Subarachnoid Hemorrhage",
          options: [
            "Subarachnoid Hemorrhage",
            "Migraine",
            "Meningitis",
            "Tension Headache",
          ],
          explanation:
            "Sudden severe 'thunderclap' headache with meningeal signs (neck stiffness, photophobia), CT showing blood in subarachnoid space, and xanthochromia on LP indicates subarachnoid hemorrhage, often from ruptured aneurysm.",
        },
        {
          id: "neuro-med-5",
          patientInfo: "37-year-old female",
          symptoms: [
            "Sudden unilateral facial droop",
            "Inability to close the affected eye",
            "Loss of taste on the anterior tongue",
            "Pain behind the ear",
            "No limb weakness or speech changes",
          ],
          labResults: "Neurologic exam: LMN facial weakness, MRI brain: no acute stroke",
          correctAnswer: "Bell's Palsy",
          options: [
            "Bell's Palsy",
            "Ischemic Stroke",
            "Myasthenia Gravis",
            "Temporal Arteritis",
          ],
          explanation:
            "Lower motor neuron facial paralysis with loss of taste and ear pain, without other neurologic deficits, is classic for Bell's palsy.",
        },
        {
          id: "neuro-med-6",
          patientInfo: "72-year-old female",
          symptoms: [
            "Severe unilateral temporal headache",
            "Scalp tenderness when combing hair",
            "Jaw pain with chewing",
            "Blurred vision",
            "Low-grade fever and fatigue",
          ],
          labResults: "ESR: 92 mm/hr, CRP: elevated, temporal artery biopsy: granulomatous inflammation",
          correctAnswer: "Giant Cell Arteritis",
          options: [
            "Giant Cell Arteritis",
            "Migraine",
            "Subarachnoid Hemorrhage",
            "Cluster Headache",
          ],
          explanation:
            "Temporal headache with jaw claudication, vision symptoms, and elevated ESR/CRP points to giant cell arteritis, confirmed by biopsy.",
        },
        {
          id: "neuro-med-7",
          patientInfo: "45-year-old female",
          symptoms: [
            "Ptosis and miosis in left eye",
            "Anhidrosis on left face",
            "Recent neck trauma",
            "Mild headache",
            "No limb weakness",
          ],
          labResults: "MRI/MRA neck: left carotid artery dissection",
          correctAnswer: "Horner Syndrome",
          options: [
            "Horner Syndrome",
            "Third Nerve Palsy",
            "Myasthenia Gravis",
            "Cluster Headache",
          ],
          explanation:
            "Ptosis, miosis, and anhidrosis (Horner triad) with carotid dissection on imaging. Carotid dissection is a common cause in younger patients.",
        },
        {
          id: "neuro-med-8",
          patientInfo: "60-year-old male",
          symptoms: [
            "Sudden vertigo",
            "Nausea and vomiting",
            "Difficulty walking",
            "Diplopia",
            "Dysarthria",
          ],
          labResults: "MRI brain: acute infarct in cerebellum and brainstem",
          correctAnswer: "Posterior Circulation Stroke",
          options: [
            "Posterior Circulation Stroke",
            "Benign Positional Vertigo",
            "Vestibular Neuritis",
            "Meniere's Disease",
          ],
          explanation:
            "Vertigo with brainstem signs (diplopia, dysarthria) and cerebellar signs indicates posterior circulation stroke rather than peripheral vertigo.",
        },
        {
          id: "neuro-med-9",
          patientInfo: "35-year-old female",
          symptoms: [
            "Acute vision loss in left eye",
            "Pain with eye movement",
            "Color vision affected",
            "Afferent pupillary defect",
            "History of numbness episodes",
          ],
          labResults: "MRI brain/orbits: optic nerve enhancement, multiple periventricular white matter lesions",
          correctAnswer: "Optic Neuritis (Multiple Sclerosis)",
          options: [
            "Optic Neuritis (Multiple Sclerosis)",
            "Retinal Detachment",
            "Central Retinal Artery Occlusion",
            "Anterior Ischemic Optic Neuropathy",
          ],
          explanation:
            "Painful vision loss with RAPD, optic nerve enhancement, and white matter lesions suggests optic neuritis, often first presentation of MS.",
        },
        {
          id: "neuro-med-10",
          patientInfo: "68-year-old male",
          symptoms: [
            "Right arm and leg weakness",
            "Symptoms resolved in 30 minutes",
            "No residual deficits",
            "Speech was slurred during episode",
            "History of hypertension and diabetes",
          ],
          labResults: "MRI brain: no acute infarct, Carotid ultrasound: 80% stenosis left ICA",
          correctAnswer: "Transient Ischemic Attack",
          options: [
            "Transient Ischemic Attack",
            "Complex Migraine",
            "Focal Seizure",
            "Hypoglycemia",
          ],
          explanation:
            "Brief neurological deficit that completely resolves within 24 hours (usually minutes) with vascular risk factors defines TIA.",
        },
        {
          id: "neuro-med-11",
          patientInfo: "50-year-old male",
          symptoms: [
            "Progressive difficulty walking",
            "Leg stiffness and spasticity",
            "Hyperreflexia in legs",
            "Upgoing toes bilaterally",
            "No sensory loss",
          ],
          labResults: "MRI spine: normal, EMG: no lower motor neuron findings, Genetic testing: positive for hereditary spastic paraplegia",
          correctAnswer: "Hereditary Spastic Paraplegia",
          options: [
            "Hereditary Spastic Paraplegia",
            "Multiple Sclerosis",
            "Cervical Myelopathy",
            "ALS",
          ],
          explanation:
            "Progressive spastic paraparesis with isolated upper motor neuron signs and positive genetic testing confirms hereditary spastic paraplegia.",
        },
        {
          id: "neuro-med-12",
          patientInfo: "32-year-old female",
          symptoms: [
            "Severe headache for 2 weeks",
            "Papilledema on exam",
            "Visual obscurations",
            "Pulsatile tinnitus",
            "Obese female",
          ],
          labResults: "MRI brain: normal, MRV: normal, Lumbar puncture opening pressure: 32 cm H2O",
          correctAnswer: "Idiopathic Intracranial Hypertension",
          options: [
            "Idiopathic Intracranial Hypertension",
            "Brain Tumor",
            "Cerebral Venous Thrombosis",
            "Meningitis",
          ],
          explanation:
            "Young obese female with headache, papilledema, elevated opening pressure, and normal imaging is classic for idiopathic intracranial hypertension.",
        },
        {
          id: "neuro-med-13",
          patientInfo: "55-year-old male",
          symptoms: [
            "Acting out dreams during sleep",
            "Violent movements while sleeping",
            "No memory of events",
            "Spouse reports punching in sleep",
            "Mild daytime sleepiness",
          ],
          labResults: "Polysomnography: REM sleep without atonia",
          correctAnswer: "REM Sleep Behavior Disorder",
          options: [
            "REM Sleep Behavior Disorder",
            "Sleepwalking",
            "Night Terrors",
            "Nocturnal Seizures",
          ],
          explanation:
            "Dream enactment behavior with loss of REM atonia on sleep study indicates REM sleep behavior disorder, often precedes Parkinson's disease.",
        },
        {
          id: "neuro-med-14",
          patientInfo: "40-year-old female",
          symptoms: [
            "Sudden severe headache",
            "Seizure at onset",
            "Focal neurological deficits",
            "Post-partum day 5",
            "History of preeclampsia",
          ],
          labResults: "MRV: cerebral venous sinus thrombosis",
          correctAnswer: "Cerebral Venous Thrombosis",
          options: [
            "Cerebral Venous Thrombosis",
            "Subarachnoid Hemorrhage",
            "Eclampsia",
            "Posterior Reversible Encephalopathy Syndrome",
          ],
          explanation:
            "Postpartum headache with seizure and focal deficits with venous thrombosis on imaging. Pregnancy/postpartum is a major risk factor.",
        },
        {
          id: "neuro-med-15",
          patientInfo: "28-year-old male",
          symptoms: [
            "Weakness of face, arm, and leg on same side",
            "Dysarthria",
            "Used cocaine recently",
            "Severe headache at onset",
            "Found by roommate",
          ],
          labResults: "CT head: intracerebral hemorrhage in basal ganglia",
          correctAnswer: "Hemorrhagic Stroke (Drug-Induced)",
          options: [
            "Hemorrhagic Stroke (Drug-Induced)",
            "Ischemic Stroke",
            "Seizure with Todd's Paralysis",
            "Drug Intoxication",
          ],
          explanation:
            "Young patient with acute neurological deficit and ICH in basal ganglia after cocaine use - cocaine causes hypertensive hemorrhage.",
        },
        {
          id: "neuro-med-16",
          patientInfo: "65-year-old male",
          symptoms: [
            "Visual hallucinations of people",
            "Fluctuating cognition",
            "Parkinsonism",
            "REM sleep behavior disorder",
            "Falls",
          ],
          labResults: "DaTscan: reduced dopamine uptake, MRI: preserved hippocampal volume",
          correctAnswer: "Lewy Body Dementia",
          options: [
            "Lewy Body Dementia",
            "Alzheimer's Disease",
            "Parkinson's Disease Dementia",
            "Vascular Dementia",
          ],
          explanation:
            "Core features of visual hallucinations, fluctuating cognition, and parkinsonism with supportive RBD suggests Lewy body dementia.",
        },
        {
          id: "neuro-med-17",
          patientInfo: "70-year-old male",
          symptoms: [
            "Stepwise cognitive decline",
            "Gait disturbance",
            "Pseudobulbar affect",
            "Focal neurological signs",
            "History of multiple strokes",
          ],
          labResults: "MRI brain: multiple lacunar infarcts, extensive white matter disease",
          correctAnswer: "Vascular Dementia",
          options: [
            "Vascular Dementia",
            "Alzheimer's Disease",
            "Frontotemporal Dementia",
            "Normal Pressure Hydrocephalus",
          ],
          explanation:
            "Stepwise decline with focal signs, gait problems, and multiple infarcts on imaging indicates vascular dementia.",
        },
        {
          id: "neuro-med-18",
          patientInfo: "58-year-old male",
          symptoms: [
            "Behavioral changes and disinhibition",
            "Apathy",
            "Loss of empathy",
            "Compulsive eating",
            "Relatively preserved memory early on",
          ],
          labResults: "MRI brain: frontal and temporal lobe atrophy, PET: frontal hypometabolism",
          correctAnswer: "Frontotemporal Dementia",
          options: [
            "Frontotemporal Dementia",
            "Alzheimer's Disease",
            "Depression",
            "Bipolar Disorder",
          ],
          explanation:
            "Early behavioral changes with preserved memory, frontal/temporal atrophy, and frontal hypometabolism indicate frontotemporal dementia.",
        },
        {
          id: "neuro-med-19",
          patientInfo: "45-year-old male",
          symptoms: [
            "Proximal muscle weakness",
            "Difficulty climbing stairs",
            "Muscle pain",
            "Heliotrope rash around eyes",
            "Gottron papules on knuckles",
          ],
          labResults: "CK: markedly elevated, EMG: myopathic pattern, Muscle biopsy: inflammatory infiltrate",
          correctAnswer: "Dermatomyositis",
          options: [
            "Dermatomyositis",
            "Polymyositis",
            "Inclusion Body Myositis",
            "Muscular Dystrophy",
          ],
          explanation:
            "Proximal weakness with characteristic rash (heliotrope, Gottron papules), elevated CK, and inflammatory biopsy confirm dermatomyositis.",
        },
        {
          id: "neuro-med-20",
          patientInfo: "62-year-old female",
          symptoms: [
            "Progressive difficulty swallowing",
            "Nasal regurgitation",
            "Weak cough",
            "Hoarse voice",
            "Weight loss",
          ],
          labResults: "Laryngoscopy: vocal cord paralysis, MRI: skull base lesion affecting cranial nerves IX, X, XI",
          correctAnswer: "Jugular Foramen Syndrome",
          options: [
            "Jugular Foramen Syndrome",
            "Myasthenia Gravis",
            "ALS",
            "Stroke",
          ],
          explanation:
            "Combined dysfunction of CN IX, X, XI with skull base lesion indicates jugular foramen syndrome (Vernet syndrome).",
        },
      ],
      hard: [
        {
          id: "neuro-hard-1",
          patientInfo: "55-year-old male",
          symptoms: [
            "Progressive muscle weakness starting in hands",
            "Muscle twitching (fasciculations)",
            "Difficulty swallowing",
            "Slurred speech",
            "No sensory loss or pain",
          ],
          labResults:
            "EMG: widespread denervation, Motor neuron disease pattern",
          correctAnswer: "Amyotrophic Lateral Sclerosis (ALS)",
          options: [
            "Amyotrophic Lateral Sclerosis (ALS)",
            "Myasthenia Gravis",
            "Guillain-Barré Syndrome",
            "Cervical Myelopathy",
          ],
          explanation:
            "Progressive motor neuron disease with both upper (spasticity, hyperreflexia) and lower motor neuron signs (fasciculations, atrophy), bulbar symptoms, and preserved sensation is diagnostic of ALS (Lou Gehrig's disease).",
        },
        {
          id: "neuro-hard-2",
          patientInfo: "35-year-old male",
          symptoms: [
            "Progressive ascending weakness starting in legs",
            "Weakness developed over 1 week",
            "Tingling in feet and hands",
            "Recent diarrheal illness 2 weeks ago",
            "Difficulty breathing",
          ],
          labResults: "CSF: elevated protein with normal cell count (albuminocytologic dissociation), EMG: demyelinating pattern",
          correctAnswer: "Guillain-Barré Syndrome",
          options: [
            "Guillain-Barré Syndrome",
            "Transverse Myelitis",
            "Myasthenia Gravis",
            "Poliomyelitis",
          ],
          explanation:
            "Ascending weakness following infection, areflexia, CSF showing albuminocytologic dissociation, and demyelinating EMG pattern are classic for Guillain-Barré syndrome. Respiratory involvement requires ICU monitoring.",
        },
        {
          id: "neuro-hard-3",
          patientInfo: "72-year-old male",
          symptoms: [
            "Gait difficulty with wide-based walking",
            "Urinary incontinence",
            "Memory problems and cognitive slowing",
            "Symptoms developed gradually over months",
            "No headaches",
          ],
          labResults: "CT head: enlarged ventricles out of proportion to cortical atrophy, LP: opening pressure normal, symptoms improve after large volume tap",
          correctAnswer: "Normal Pressure Hydrocephalus",
          options: [
            "Normal Pressure Hydrocephalus",
            "Parkinson's Disease",
            "Alzheimer's Disease",
            "Vascular Dementia",
          ],
          explanation:
            "Classic triad of gait disturbance, urinary incontinence, and dementia with ventriculomegaly and improvement after LP is diagnostic of normal pressure hydrocephalus. It's one of the treatable causes of dementia.",
        },
        {
          id: "neuro-hard-4",
          patientInfo: "65-year-old female",
          symptoms: [
            "Visual hallucinations of people and animals",
            "Fluctuating cognition and attention",
            "REM sleep behavior disorder",
            "Parkinsonism (mild tremor, rigidity)",
            "Severe sensitivity to antipsychotic medications",
          ],
          labResults: "MRI: relatively preserved hippocampi, DaTscan: reduced dopamine transporter uptake",
          correctAnswer: "Lewy Body Dementia",
          options: [
            "Lewy Body Dementia",
            "Parkinson's Disease Dementia",
            "Alzheimer's Disease",
            "Delirium",
          ],
          explanation:
            "Core features of visual hallucinations, fluctuating cognition, parkinsonism, and REM sleep behavior disorder with severe neuroleptic sensitivity are diagnostic of Lewy body dementia. It differs from PD dementia by cognitive symptoms preceding or occurring within 1 year of motor symptoms.",
        },
        {
          id: "neuro-hard-5",
          patientInfo: "59-year-old male",
          symptoms: [
            "Rapidly progressive dementia over months",
            "Myoclonus",
            "Ataxia and gait instability",
            "Visual disturbances",
            "Akinetic mutism in late stages",
          ],
          labResults: "EEG: periodic sharp wave complexes, MRI brain: cortical ribboning",
          correctAnswer: "Creutzfeldt-Jakob Disease",
          options: [
            "Creutzfeldt-Jakob Disease",
            "Lewy Body Dementia",
            "Alzheimer's Disease",
            "Normal Pressure Hydrocephalus",
          ],
          explanation:
            "Rapidly progressive dementia with myoclonus and characteristic EEG/MRI findings is classic for Creutzfeldt-Jakob disease.",
        },
        {
          id: "neuro-hard-6",
          patientInfo: "46-year-old male",
          symptoms: [
            "Involuntary jerking movements (chorea)",
            "Personality changes and depression",
            "Progressive cognitive decline",
            "Family history of similar illness",
            "Onset in early adulthood",
          ],
          labResults: "Genetic testing: CAG repeat expansion on HTT gene",
          correctAnswer: "Huntington Disease",
          options: [
            "Huntington Disease",
            "Parkinson Disease",
            "Wilson Disease",
            "Alzheimer's Disease",
          ],
          explanation:
            "Chorea, psychiatric changes, and cognitive decline with autosomal dominant family history and CAG expansion confirm Huntington disease.",
        },
        {
          id: "neuro-hard-7",
          patientInfo: "30-year-old male",
          symptoms: [
            "Tremor and dystonia",
            "Psychiatric symptoms",
            "Kayser-Fleischer rings on slit lamp exam",
            "Liver dysfunction",
            "Young age onset movement disorder",
          ],
          labResults: "Ceruloplasmin: low, 24-hour urine copper: elevated, Liver biopsy: elevated copper",
          correctAnswer: "Wilson Disease",
          options: [
            "Wilson Disease",
            "Huntington Disease",
            "Parkinson Disease",
            "Hepatic Encephalopathy",
          ],
          explanation:
            "Young onset movement disorder with psychiatric symptoms, KF rings, low ceruloplasmin, and elevated copper confirms Wilson disease.",
        },
        {
          id: "neuro-hard-8",
          patientInfo: "55-year-old female",
          symptoms: [
            "Rapidly progressive dementia over weeks",
            "Myoclonus",
            "Visual disturbances",
            "Ataxia",
            "Akinetic mutism",
          ],
          labResults: "EEG: periodic sharp wave complexes, MRI: cortical ribboning on DWI, CSF 14-3-3: positive",
          correctAnswer: "Creutzfeldt-Jakob Disease",
          options: [
            "Creutzfeldt-Jakob Disease",
            "Alzheimer's Disease",
            "Autoimmune Encephalitis",
            "Viral Encephalitis",
          ],
          explanation:
            "Rapidly progressive dementia with myoclonus, characteristic EEG, MRI findings, and positive 14-3-3 protein suggests CJD (prion disease).",
        },
        {
          id: "neuro-hard-9",
          patientInfo: "35-year-old female",
          symptoms: [
            "Acute psychiatric symptoms",
            "Seizures",
            "Memory problems",
            "Dyskinesias",
            "Autonomic instability",
          ],
          labResults: "CSF: lymphocytic pleocytosis, MRI: medial temporal hyperintensity, NMDA receptor antibodies: positive",
          correctAnswer: "Anti-NMDA Receptor Encephalitis",
          options: [
            "Anti-NMDA Receptor Encephalitis",
            "Viral Encephalitis",
            "Schizophrenia",
            "Status Epilepticus",
          ],
          explanation:
            "Young female with psychiatric symptoms, seizures, movement disorder, and NMDA receptor antibodies has autoimmune encephalitis. Check for ovarian teratoma.",
        },
        {
          id: "neuro-hard-10",
          patientInfo: "65-year-old male",
          symptoms: [
            "Proximal leg weakness",
            "Autonomic symptoms (dry mouth)",
            "Strength improves with exercise",
            "Diminished reflexes that improve with exercise",
            "Heavy smoker",
          ],
          labResults: "Voltage-gated calcium channel antibodies: positive, EMG: incremental response on repetitive stimulation, CT chest: lung mass",
          correctAnswer: "Lambert-Eaton Myasthenic Syndrome",
          options: [
            "Lambert-Eaton Myasthenic Syndrome",
            "Myasthenia Gravis",
            "Polymyositis",
            "Botulism",
          ],
          explanation:
            "Proximal weakness improving with exercise, VGCC antibodies, incremental EMG response, and lung cancer suggest paraneoplastic LEMS.",
        },
        {
          id: "neuro-hard-11",
          patientInfo: "58-year-old female",
          symptoms: [
            "Severe ataxia over weeks",
            "Opsoclonus (chaotic eye movements)",
            "Myoclonus",
            "Recent unintentional weight loss",
            "Encephalopathy",
          ],
          labResults: "Anti-Yo antibodies: positive, CT chest/abdomen/pelvis: ovarian mass",
          correctAnswer: "Paraneoplastic Cerebellar Degeneration",
          options: [
            "Paraneoplastic Cerebellar Degeneration",
            "Multiple Sclerosis",
            "Stroke",
            "Wernicke Encephalopathy",
          ],
          explanation:
            "Subacute cerebellar ataxia with anti-Yo antibodies and ovarian mass indicates paraneoplastic cerebellar degeneration.",
        },
        {
          id: "neuro-hard-12",
          patientInfo: "42-year-old male",
          symptoms: [
            "Sensory ataxia",
            "Loss of position and vibration sense",
            "Positive Romberg sign",
            "Areflexia",
            "Lancinating pains in legs",
          ],
          labResults: "VDRL/RPR: positive, CSF VDRL: positive, CSF pleocytosis",
          correctAnswer: "Tabes Dorsalis (Neurosyphilis)",
          options: [
            "Tabes Dorsalis (Neurosyphilis)",
            "Vitamin B12 Deficiency",
            "Friedreich Ataxia",
            "Multiple Sclerosis",
          ],
          explanation:
            "Sensory ataxia, lightning pains, Argyll Robertson pupils, and positive syphilis serology indicate tabes dorsalis (late neurosyphilis).",
        },
        {
          id: "neuro-hard-13",
          patientInfo: "25-year-old male",
          symptoms: [
            "Progressive ataxia since childhood",
            "Cardiomyopathy",
            "Diabetes",
            "Pes cavus and scoliosis",
            "Loss of reflexes",
          ],
          labResults: "Genetic testing: GAA repeat expansion in FXN gene, Echo: hypertrophic cardiomyopathy",
          correctAnswer: "Friedreich Ataxia",
          options: [
            "Friedreich Ataxia",
            "Spinocerebellar Ataxia",
            "Multiple Sclerosis",
            "Charcot-Marie-Tooth Disease",
          ],
          explanation:
            "Young onset ataxia with cardiomyopathy, diabetes, skeletal deformities, and FXN gene mutation confirms Friedreich ataxia.",
        },
        {
          id: "neuro-hard-14",
          patientInfo: "50-year-old female",
          symptoms: [
            "Facial pain in V1 distribution",
            "Numbness over forehead",
            "Diplopia",
            "Ptosis",
            "Headache",
          ],
          labResults: "MRI: mass in cavernous sinus",
          correctAnswer: "Cavernous Sinus Syndrome",
          options: [
            "Cavernous Sinus Syndrome",
            "Trigeminal Neuralgia",
            "Cluster Headache",
            "Tolosa-Hunt Syndrome",
          ],
          explanation:
            "Combined dysfunction of CN III, IV, V1, V2, VI with mass in cavernous sinus indicates cavernous sinus syndrome.",
        },
        {
          id: "neuro-hard-15",
          patientInfo: "38-year-old male",
          symptoms: [
            "Acute flaccid paralysis",
            "Descending weakness",
            "Diplopia and ptosis first",
            "Dilated unreactive pupils",
            "Ate home-canned food yesterday",
          ],
          labResults: "Stool toxin assay: Clostridium botulinum toxin positive, EMG: decremental response",
          correctAnswer: "Botulism",
          options: [
            "Botulism",
            "Guillain-Barré Syndrome",
            "Myasthenia Gravis",
            "Tick Paralysis",
          ],
          explanation:
            "Descending paralysis starting with cranial nerves, dilated pupils, and history of canned food consumption indicates foodborne botulism.",
        },
        {
          id: "neuro-hard-16",
          patientInfo: "60-year-old male",
          symptoms: [
            "Slowly progressive asymmetric limb rigidity",
            "Alien limb phenomenon",
            "Apraxia",
            "Cortical sensory loss",
            "Myoclonus",
          ],
          labResults: "MRI: asymmetric cortical atrophy, PET: asymmetric hypometabolism",
          correctAnswer: "Corticobasal Degeneration",
          options: [
            "Corticobasal Degeneration",
            "Parkinson's Disease",
            "Progressive Supranuclear Palsy",
            "Multiple System Atrophy",
          ],
          explanation:
            "Asymmetric rigidity, alien limb, apraxia, and cortical sensory loss with asymmetric cortical atrophy suggest corticobasal degeneration.",
        },
        {
          id: "neuro-hard-17",
          patientInfo: "65-year-old male",
          symptoms: [
            "Early falls (backward)",
            "Vertical gaze palsy",
            "Axial rigidity greater than limb",
            "Pseudobulbar affect",
            "Poor response to levodopa",
          ],
          labResults: "MRI: midbrain atrophy (hummingbird sign)",
          correctAnswer: "Progressive Supranuclear Palsy",
          options: [
            "Progressive Supranuclear Palsy",
            "Parkinson's Disease",
            "Corticobasal Degeneration",
            "Multiple System Atrophy",
          ],
          explanation:
            "Early falls, vertical gaze palsy, axial rigidity, and midbrain atrophy (hummingbird sign) indicate progressive supranuclear palsy.",
        },
        {
          id: "neuro-hard-18",
          patientInfo: "55-year-old male",
          symptoms: [
            "Parkinsonism with poor levodopa response",
            "Orthostatic hypotension",
            "Urinary incontinence",
            "Cerebellar ataxia",
            "REM sleep behavior disorder",
          ],
          labResults: "MRI: hot cross bun sign in pons, cerebellar atrophy",
          correctAnswer: "Multiple System Atrophy",
          options: [
            "Multiple System Atrophy",
            "Parkinson's Disease",
            "Progressive Supranuclear Palsy",
            "Lewy Body Dementia",
          ],
          explanation:
            "Parkinsonism with autonomic failure, cerebellar signs, poor levodopa response, and hot cross bun sign suggest multiple system atrophy.",
        },
        {
          id: "neuro-hard-19",
          patientInfo: "12-year-old male",
          symptoms: [
            "Progressive weakness and wasting of legs",
            "High-arched feet (pes cavus)",
            "Hammer toes",
            "Sensory loss in stocking distribution",
            "Family history of similar condition",
          ],
          labResults: "Nerve conduction studies: demyelinating neuropathy, Genetic testing: PMP22 duplication",
          correctAnswer: "Charcot-Marie-Tooth Disease",
          options: [
            "Charcot-Marie-Tooth Disease",
            "Guillain-Barré Syndrome",
            "Friedreich Ataxia",
            "Muscular Dystrophy",
          ],
          explanation:
            "Young onset hereditary neuropathy with pes cavus, distal weakness, demyelinating NCS, and PMP22 mutation confirms CMT type 1A.",
        },
        {
          id: "neuro-hard-20",
          patientInfo: "45-year-old female",
          symptoms: [
            "Severe neuropathic pain",
            "Autonomic neuropathy",
            "Cardiomyopathy",
            "Carpal tunnel syndrome",
            "Family history of amyloidosis",
          ],
          labResults: "Nerve biopsy: amyloid deposits, Genetic testing: TTR mutation, Echo: increased wall thickness",
          correctAnswer: "Hereditary Transthyretin Amyloidosis",
          options: [
            "Hereditary Transthyretin Amyloidosis",
            "Diabetic Neuropathy",
            "AL Amyloidosis",
            "Chronic Inflammatory Demyelinating Polyneuropathy",
          ],
          explanation:
            "Familial neuropathy with cardiomyopathy, carpal tunnel, amyloid on biopsy, and TTR mutation confirms hereditary ATTR amyloidosis.",
        },
      ],
    },
    endocrinology: {
      easy: [
        {
          id: "endo-easy-1",
          patientInfo: "35-year-old female",
          symptoms: [
            "Persistent fatigue for 3 months",
            "Weight gain despite normal diet",
            "Cold intolerance",
            "Dry skin and hair loss",
            "Constipation",
          ],
          labResults: "TSH: elevated, Free T4: low",
          correctAnswer: "Hypothyroidism",
          options: [
            "Hypothyroidism",
            "Depression",
            "Chronic Fatigue Syndrome",
            "Anemia",
          ],
          explanation:
            "Elevated TSH with low Free T4 indicates primary hypothyroidism. The symptoms of fatigue, weight gain, cold intolerance, dry skin, and constipation are classic signs of an underactive thyroid.",
        },
        {
          id: "endo-easy-2",
          patientInfo: "22-year-old female",
          symptoms: [
            "Excessive thirst and urination",
            "Unexplained weight loss",
            "Increased hunger",
            "Blurred vision",
            "Fatigue",
          ],
          labResults: "Fasting glucose: 250 mg/dL, HbA1c: 9.5%",
          correctAnswer: "Type 1 Diabetes Mellitus",
          options: [
            "Type 1 Diabetes Mellitus",
            "Type 2 Diabetes Mellitus",
            "Diabetes Insipidus",
            "Hyperthyroidism",
          ],
          explanation:
            "Young age with classic symptoms (polyuria, polydipsia, polyphagia) and significantly elevated blood glucose with high HbA1c indicates Type 1 Diabetes Mellitus, likely due to autoimmune destruction of pancreatic beta cells.",
        },
        {
          id: "endo-easy-3",
          patientInfo: "32-year-old female",
          symptoms: [
            "Unexplained weight loss despite increased appetite",
            "Rapid heart rate and palpitations",
            "Heat intolerance and excessive sweating",
            "Tremor in hands",
            "Bulging eyes",
          ],
          labResults: "TSH: suppressed (<0.01), Free T4: elevated, TSI antibodies: positive",
          correctAnswer: "Graves' Disease (Hyperthyroidism)",
          options: [
            "Graves' Disease (Hyperthyroidism)",
            "Anxiety Disorder",
            "Pheochromocytoma",
            "Thyroid Cancer",
          ],
          explanation:
            "Suppressed TSH with elevated T4, positive TSI antibodies, and exophthalmos (bulging eyes) is pathognomonic for Graves' disease, an autoimmune cause of hyperthyroidism.",
        },
        {
          id: "endo-easy-4",
          patientInfo: "55-year-old obese male",
          symptoms: [
            "Increased thirst and urination",
            "Fatigue",
            "Slow-healing wounds",
            "Tingling in feet",
            "Family history of diabetes",
          ],
          labResults: "Fasting glucose: 145 mg/dL, HbA1c: 7.8%, C-peptide: elevated",
          correctAnswer: "Type 2 Diabetes Mellitus",
          options: [
            "Type 2 Diabetes Mellitus",
            "Type 1 Diabetes Mellitus",
            "Prediabetes",
            "Metabolic Syndrome",
          ],
          explanation:
            "Older patient with obesity, family history, elevated glucose/HbA1c, and elevated C-peptide (indicating insulin resistance, not deficiency) is classic for Type 2 Diabetes Mellitus.",
        },
        {
          id: "endo-easy-5",
          patientInfo: "29-year-old female",
          symptoms: [
            "Excessive thirst",
            "Large volumes of dilute urine",
            "Wakes multiple times at night to urinate",
            "No weight loss",
            "Recent head trauma 2 months ago",
          ],
          labResults: "Serum sodium: 150 mEq/L, urine osmolality: low, water deprivation test: no concentration",
          correctAnswer: "Diabetes Insipidus",
          options: [
            "Diabetes Insipidus",
            "Type 1 Diabetes Mellitus",
            "Psychogenic Polydipsia",
            "Hyperthyroidism",
          ],
          explanation:
            "Polyuria with dilute urine, hypernatremia, and failure to concentrate urine on water deprivation suggests diabetes insipidus, often central after head trauma.",
        },
        {
          id: "endo-easy-6",
          patientInfo: "26-year-old female",
          symptoms: [
            "Sweating and tremor",
            "Palpitations",
            "Hunger and irritability",
            "Lightheadedness",
            "Symptoms improve after eating",
          ],
          labResults: "Glucose: 52 mg/dL during symptoms, resolves with glucose",
          correctAnswer: "Hypoglycemia",
          options: [
            "Hypoglycemia",
            "Hyperthyroidism",
            "Panic Attack",
            "Anemia",
          ],
          explanation:
            "Adrenergic symptoms with low glucose that resolve after eating indicate hypoglycemia.",
        },
        {
          id: "endo-easy-7",
          patientInfo: "55-year-old female",
          symptoms: [
            "Bone pain",
            "Kidney stones",
            "Constipation",
            "Fatigue and depression",
            "Abdominal groans",
          ],
          labResults: "Calcium: elevated, PTH: elevated, Phosphorus: low",
          correctAnswer: "Primary Hyperparathyroidism",
          options: [
            "Primary Hyperparathyroidism",
            "Hypercalcemia of Malignancy",
            "Vitamin D Toxicity",
            "Sarcoidosis",
          ],
          explanation:
            "Stones, bones, groans, and psychiatric moans with elevated calcium and PTH indicate primary hyperparathyroidism.",
        },
        {
          id: "endo-easy-8",
          patientInfo: "45-year-old male",
          symptoms: [
            "Fatigue and weakness",
            "Muscle cramps",
            "Numbness and tingling around mouth",
            "Positive Chvostek sign",
            "Recent thyroid surgery",
          ],
          labResults: "Calcium: low, Phosphorus: high, PTH: low",
          correctAnswer: "Hypoparathyroidism",
          options: [
            "Hypoparathyroidism",
            "Vitamin D Deficiency",
            "Chronic Kidney Disease",
            "Hypomagnesemia",
          ],
          explanation:
            "Low calcium with low PTH after thyroid surgery indicates surgical hypoparathyroidism. Chvostek sign reflects neuromuscular irritability.",
        },
        {
          id: "endo-easy-9",
          patientInfo: "60-year-old male",
          symptoms: [
            "Polyuria and polydipsia",
            "Fatigue",
            "Blurred vision",
            "Slow-healing wounds",
            "BMI 32, family history of diabetes",
          ],
          labResults: "Fasting glucose: 145 mg/dL, HbA1c: 8.2%, C-peptide: normal",
          correctAnswer: "Type 2 Diabetes Mellitus",
          options: [
            "Type 2 Diabetes Mellitus",
            "Type 1 Diabetes Mellitus",
            "Diabetes Insipidus",
            "Gestational Diabetes",
          ],
          explanation:
            "Classic symptoms with elevated fasting glucose and HbA1c, normal C-peptide, obesity, and family history indicate Type 2 diabetes.",
        },
        {
          id: "endo-easy-10",
          patientInfo: "18-year-old male",
          symptoms: [
            "Polyuria and polydipsia",
            "Unintentional weight loss",
            "Fatigue",
            "Nausea and vomiting",
            "Fruity breath odor",
          ],
          labResults: "Glucose: 450 mg/dL, pH: 7.25, Ketones: positive, C-peptide: very low",
          correctAnswer: "Type 1 Diabetes with DKA",
          options: [
            "Type 1 Diabetes with DKA",
            "Type 2 Diabetes",
            "Hyperosmolar Hyperglycemic State",
            "Gastroenteritis",
          ],
          explanation:
            "Young patient with hyperglycemia, ketoacidosis, low C-peptide, and classic symptoms indicates new-onset Type 1 diabetes presenting with DKA.",
        },
        {
          id: "endo-easy-11",
          patientInfo: "50-year-old female",
          symptoms: [
            "Neck swelling noticed by patient",
            "No pain or tenderness",
            "No difficulty swallowing",
            "Normal thyroid function",
            "Incidental finding on exam",
          ],
          labResults: "TSH: normal, Ultrasound: 2 cm thyroid nodule, FNA: benign colloid nodule",
          correctAnswer: "Benign Thyroid Nodule",
          options: [
            "Benign Thyroid Nodule",
            "Thyroid Cancer",
            "Thyroiditis",
            "Goiter",
          ],
          explanation:
            "Asymptomatic thyroid nodule with normal thyroid function and benign FNA cytology indicates a benign thyroid nodule requiring follow-up only.",
        },
        {
          id: "endo-easy-12",
          patientInfo: "65-year-old male",
          symptoms: [
            "Erectile dysfunction",
            "Decreased libido",
            "Fatigue and depression",
            "Decreased muscle mass",
            "Hot flashes",
          ],
          labResults: "Total testosterone: low on two occasions, LH: low, FSH: low",
          correctAnswer: "Male Hypogonadism",
          options: [
            "Male Hypogonadism",
            "Depression",
            "Hypothyroidism",
            "Normal Aging",
          ],
          explanation:
            "Low testosterone with low gonadotropins indicates secondary (central) hypogonadism requiring pituitary evaluation.",
        },
        {
          id: "endo-easy-13",
          patientInfo: "28-year-old female",
          symptoms: [
            "Irregular menstrual periods",
            "Hirsutism (facial hair)",
            "Acne",
            "Weight gain",
            "Difficulty conceiving",
          ],
          labResults: "LH/FSH ratio: elevated, Testosterone: mildly elevated, Ultrasound: polycystic ovaries",
          correctAnswer: "Polycystic Ovary Syndrome",
          options: [
            "Polycystic Ovary Syndrome",
            "Cushing Syndrome",
            "Congenital Adrenal Hyperplasia",
            "Hypothyroidism",
          ],
          explanation:
            "Irregular periods, hyperandrogenism (hirsutism, acne), and polycystic ovaries meet criteria for PCOS.",
        },
        {
          id: "endo-easy-14",
          patientInfo: "70-year-old female",
          symptoms: [
            "Back pain",
            "Loss of height",
            "Kyphosis",
            "Recent wrist fracture from minor fall",
            "Postmenopausal for 20 years",
          ],
          labResults: "DEXA scan: T-score -3.0 at lumbar spine, Vitamin D: low, Calcium: normal",
          correctAnswer: "Osteoporosis",
          options: [
            "Osteoporosis",
            "Osteomalacia",
            "Multiple Myeloma",
            "Hyperparathyroidism",
          ],
          explanation:
            "Low trauma fracture with T-score ≤-2.5 on DEXA confirms osteoporosis. Postmenopausal status is major risk factor.",
        },
        {
          id: "endo-easy-15",
          patientInfo: "35-year-old male",
          symptoms: [
            "Extreme thirst",
            "Urinating 10 liters per day",
            "Waking multiple times at night to urinate",
            "Preference for ice-cold water",
            "Recent head injury",
          ],
          labResults: "Serum sodium: 148 mEq/L, Serum osmolality: elevated, Urine osmolality: very low",
          correctAnswer: "Central Diabetes Insipidus",
          options: [
            "Central Diabetes Insipidus",
            "Nephrogenic Diabetes Insipidus",
            "Primary Polydipsia",
            "Type 2 Diabetes Mellitus",
          ],
          explanation:
            "Polyuria with dilute urine, elevated serum osmolality, and history of head injury suggests central diabetes insipidus (ADH deficiency).",
        },
        {
          id: "endo-easy-16",
          patientInfo: "42-year-old female",
          symptoms: [
            "Enlarged hands and feet",
            "Facial feature changes",
            "Headaches",
            "Visual field defects",
            "Joint pain",
          ],
          labResults: "IGF-1: elevated, GH suppression test: GH not suppressed, MRI: pituitary macroadenoma",
          correctAnswer: "Acromegaly",
          options: [
            "Acromegaly",
            "Gigantism",
            "Cushing Disease",
            "Prolactinoma",
          ],
          explanation:
            "Acral enlargement, soft tissue changes, elevated IGF-1, unsuppressed GH, and pituitary adenoma confirm acromegaly.",
        },
        {
          id: "endo-easy-17",
          patientInfo: "30-year-old female",
          symptoms: [
            "Galactorrhea (breast milk discharge)",
            "Amenorrhea",
            "Headaches",
            "Decreased libido",
            "Infertility",
          ],
          labResults: "Prolactin: markedly elevated, MRI: pituitary microadenoma",
          correctAnswer: "Prolactinoma",
          options: [
            "Prolactinoma",
            "Acromegaly",
            "Hypothyroidism",
            "Pregnancy",
          ],
          explanation:
            "Galactorrhea, amenorrhea, and markedly elevated prolactin with pituitary adenoma indicate prolactinoma.",
        },
        {
          id: "endo-easy-18",
          patientInfo: "25-year-old male",
          symptoms: [
            "Tall stature since childhood",
            "Large hands and feet",
            "Headaches",
            "Visual problems",
            "Parents of normal height",
          ],
          labResults: "IGF-1: elevated, GH: elevated, MRI: pituitary macroadenoma, Bone age: open growth plates",
          correctAnswer: "Gigantism",
          options: [
            "Gigantism",
            "Acromegaly",
            "Marfan Syndrome",
            "Constitutional Tall Stature",
          ],
          explanation:
            "GH excess before growth plate closure causes gigantism (excessive linear growth), unlike acromegaly which occurs after closure.",
        },
        {
          id: "endo-easy-19",
          patientInfo: "58-year-old male",
          symptoms: [
            "Confusion",
            "Lethargy",
            "Nausea",
            "Headache",
            "Recent lung cancer diagnosis",
          ],
          labResults: "Sodium: 118 mEq/L, Serum osmolality: low, Urine osmolality: elevated, Urine sodium: elevated",
          correctAnswer: "SIADH",
          options: [
            "SIADH",
            "Adrenal Insufficiency",
            "Hypothyroidism",
            "Heart Failure",
          ],
          explanation:
            "Hyponatremia with low serum osmolality, concentrated urine, and lung cancer suggests SIADH (inappropriate ADH secretion).",
        },
        {
          id: "endo-easy-20",
          patientInfo: "45-year-old female",
          symptoms: [
            "Fatigue",
            "Weight gain",
            "Irregular periods",
            "Headaches",
            "Cold intolerance",
          ],
          labResults: "TSH: elevated, Free T4: low, Free T3: low, Anti-TPO antibodies: positive",
          correctAnswer: "Hashimoto's Thyroiditis",
          options: [
            "Hashimoto's Thyroiditis",
            "Graves' Disease",
            "Subacute Thyroiditis",
            "Iodine Deficiency",
          ],
          explanation:
            "Hypothyroidism with positive TPO antibodies is consistent with Hashimoto's (autoimmune) thyroiditis, the most common cause.",
        },
      ],
      medium: [
        {
          id: "endo-med-1",
          patientInfo: "40-year-old female",
          symptoms: [
            "Weight gain, particularly around face and trunk",
            "Purple stretch marks on abdomen",
            "Easy bruising",
            "High blood pressure",
            "Facial hair growth",
          ],
          labResults:
            "Cortisol: elevated, ACTH: elevated, Dexamethasone suppression test: positive",
          correctAnswer: "Cushing's Disease",
          options: [
            "Cushing's Disease",
            "Polycystic Ovary Syndrome",
            "Obesity",
            "Adrenal Tumor",
          ],
          explanation:
            "Central obesity with purple striae, moon facies, hirsutism, and elevated cortisol with elevated ACTH suggests Cushing's disease (pituitary adenoma causing excess ACTH production).",
        },
        {
          id: "endo-med-2",
          patientInfo: "45-year-old female",
          symptoms: [
            "Episodes of severe headaches",
            "Palpitations and sweating spells",
            "High blood pressure (episodic)",
            "Anxiety during episodes",
            "Pallor",
          ],
          labResults: "24-hour urine catecholamines: markedly elevated, CT abdomen: adrenal mass",
          correctAnswer: "Pheochromocytoma",
          options: [
            "Pheochromocytoma",
            "Panic Disorder",
            "Essential Hypertension",
            "Hyperthyroidism",
          ],
          explanation:
            "Classic triad of episodic headaches, sweating, and palpitations with paroxysmal hypertension, elevated catecholamines, and adrenal mass is diagnostic of pheochromocytoma, a catecholamine-secreting tumor.",
        },
        {
          id: "endo-med-3",
          patientInfo: "28-year-old female",
          symptoms: [
            "Irregular menstrual periods",
            "Difficulty getting pregnant",
            "Acne and oily skin",
            "Excess facial and body hair",
            "Weight gain",
          ],
          labResults: "LH:FSH ratio > 2:1, testosterone: elevated, ultrasound: bilateral ovarian cysts",
          correctAnswer: "Polycystic Ovary Syndrome (PCOS)",
          options: [
            "Polycystic Ovary Syndrome (PCOS)",
            "Cushing's Syndrome",
            "Congenital Adrenal Hyperplasia",
            "Hypothyroidism",
          ],
          explanation:
            "Menstrual irregularity, hyperandrogenism (acne, hirsutism), elevated LH:FSH ratio, and polycystic ovaries meet Rotterdam criteria for PCOS, the most common endocrine disorder in women of reproductive age.",
        },
        {
          id: "endo-med-4",
          patientInfo: "60-year-old female",
          symptoms: [
            "Bone pain and muscle weakness",
            "History of kidney stones",
            "Fatigue and depression",
            "Constipation",
            "Frequent urination",
          ],
          labResults: "Calcium: elevated (11.5 mg/dL), PTH: elevated, Vitamin D: normal",
          correctAnswer: "Primary Hyperparathyroidism",
          options: [
            "Primary Hyperparathyroidism",
            "Hypercalcemia of Malignancy",
            "Vitamin D Toxicity",
            "Sarcoidosis",
          ],
          explanation:
            "'Bones, stones, groans, and psychiatric overtones' with elevated calcium AND elevated PTH (inappropriate response) indicates primary hyperparathyroidism, usually from a parathyroid adenoma.",
        },
        {
          id: "endo-med-5",
          patientInfo: "33-year-old female",
          symptoms: [
            "Anterior neck pain and tenderness",
            "Recent viral illness",
            "Palpitations and heat intolerance",
            "Fatigue and low-grade fever",
            "Pain radiates to jaw",
          ],
          labResults: "TSH: low, Free T4: elevated, ESR: high",
          correctAnswer: "Subacute (De Quervain) Thyroiditis",
          options: [
            "Subacute (De Quervain) Thyroiditis",
            "Graves' Disease",
            "Hashimoto Thyroiditis",
            "Pheochromocytoma",
          ],
          explanation:
            "Painful thyroid after a viral illness with transient hyperthyroidism and elevated ESR is classic for subacute thyroiditis.",
        },
        {
          id: "endo-med-6",
          patientInfo: "48-year-old male",
          symptoms: [
            "Persistent hypertension",
            "Muscle weakness and cramps",
            "Frequent urination",
            "Headaches",
            "No edema",
          ],
          labResults: "Potassium: 2.9 mEq/L, aldosterone: elevated, renin: low",
          correctAnswer: "Primary Hyperaldosteronism (Conn Syndrome)",
          options: [
            "Primary Hyperaldosteronism (Conn Syndrome)",
            "Cushing's Syndrome",
            "Renal Artery Stenosis",
            "Pheochromocytoma",
          ],
          explanation:
            "Hypertension with hypokalemia and elevated aldosterone with suppressed renin suggests primary hyperaldosteronism.",
        },
        {
          id: "endo-med-7",
          patientInfo: "35-year-old female",
          symptoms: [
            "Neck pain radiating to jaw",
            "Fever and malaise",
            "Tender thyroid gland",
            "Palpitations initially",
            "Recent upper respiratory infection",
          ],
          labResults: "TSH: low, Free T4: elevated, ESR: very elevated, Thyroid uptake scan: very low uptake",
          correctAnswer: "Subacute (De Quervain) Thyroiditis",
          options: [
            "Subacute (De Quervain) Thyroiditis",
            "Graves' Disease",
            "Hashimoto's Thyroiditis",
            "Thyroid Storm",
          ],
          explanation:
            "Painful thyroid, hyperthyroid symptoms, elevated ESR, and low radioiodine uptake after URI indicate subacute thyroiditis.",
        },
        {
          id: "endo-med-8",
          patientInfo: "28-year-old female",
          symptoms: [
            "Newly discovered during pregnancy",
            "High blood glucose",
            "No prior diabetes history",
            "Family history of Type 2 diabetes",
            "Obese",
          ],
          labResults: "Oral glucose tolerance test at 26 weeks: 2-hour glucose 165 mg/dL",
          correctAnswer: "Gestational Diabetes",
          options: [
            "Gestational Diabetes",
            "Type 2 Diabetes",
            "Type 1 Diabetes",
            "Impaired Glucose Tolerance",
          ],
          explanation:
            "Abnormal OGTT during pregnancy without pre-existing diabetes defines gestational diabetes. Requires monitoring and often treatment.",
        },
        {
          id: "endo-med-9",
          patientInfo: "60-year-old male",
          symptoms: [
            "Confusion and lethargy",
            "Profound weakness",
            "Severe hyperglycemia",
            "No ketones in urine",
            "History of Type 2 diabetes",
          ],
          labResults: "Glucose: 850 mg/dL, Serum osmolality: 340 mOsm/kg, pH: 7.32, Ketones: negative",
          correctAnswer: "Hyperosmolar Hyperglycemic State",
          options: [
            "Hyperosmolar Hyperglycemic State",
            "Diabetic Ketoacidosis",
            "Lactic Acidosis",
            "Acute Stroke",
          ],
          explanation:
            "Severe hyperglycemia with hyperosmolality but no significant ketosis or acidosis indicates HHS, typically in Type 2 diabetes.",
        },
        {
          id: "endo-med-10",
          patientInfo: "25-year-old female",
          symptoms: [
            "Virilization (deepening voice)",
            "Rapid onset hirsutism",
            "Acne",
            "Clitoromegaly",
            "Amenorrhea",
          ],
          labResults: "Testosterone: markedly elevated, DHEA-S: normal, CT abdomen: adrenal mass",
          correctAnswer: "Androgen-Secreting Adrenal Tumor",
          options: [
            "Androgen-Secreting Adrenal Tumor",
            "Polycystic Ovary Syndrome",
            "Congenital Adrenal Hyperplasia",
            "Ovarian Tumor",
          ],
          explanation:
            "Rapid onset virilization with markedly elevated testosterone and adrenal mass suggests androgen-secreting adrenal tumor.",
        },
        {
          id: "endo-med-11",
          patientInfo: "50-year-old male",
          symptoms: [
            "Gynecomastia",
            "Decreased libido",
            "Fatigue",
            "Small firm testes",
            "Tall stature with long limbs",
          ],
          labResults: "Testosterone: low, FSH and LH: elevated, Karyotype: 47,XXY",
          correctAnswer: "Klinefelter Syndrome",
          options: [
            "Klinefelter Syndrome",
            "Secondary Hypogonadism",
            "Prolactinoma",
            "Androgen Insensitivity Syndrome",
          ],
          explanation:
            "Primary hypogonadism with elevated gonadotropins, small testes, gynecomastia, and 47,XXY karyotype confirms Klinefelter syndrome.",
        },
        {
          id: "endo-med-12",
          patientInfo: "16-year-old female",
          symptoms: [
            "Primary amenorrhea",
            "Short stature",
            "Webbed neck",
            "Shield chest",
            "No breast development",
          ],
          labResults: "FSH and LH: elevated, Karyotype: 45,X",
          correctAnswer: "Turner Syndrome",
          options: [
            "Turner Syndrome",
            "Kallmann Syndrome",
            "Androgen Insensitivity",
            "Constitutional Delay",
          ],
          explanation:
            "Primary amenorrhea, short stature, characteristic features, and 45,X karyotype confirm Turner syndrome.",
        },
        {
          id: "endo-med-13",
          patientInfo: "22-year-old female",
          symptoms: [
            "Primary amenorrhea",
            "Normal breast development",
            "Absent uterus on exam",
            "Blind-ending vagina",
            "Normal external genitalia",
          ],
          labResults: "Testosterone: normal male range, Karyotype: 46,XY, LH: elevated",
          correctAnswer: "Androgen Insensitivity Syndrome",
          options: [
            "Androgen Insensitivity Syndrome",
            "Turner Syndrome",
            "Mullerian Agenesis",
            "Kallmann Syndrome",
          ],
          explanation:
            "46,XY individual with female phenotype, absent uterus, and male-range testosterone indicates complete androgen insensitivity.",
        },
        {
          id: "endo-med-14",
          patientInfo: "30-year-old male",
          symptoms: [
            "Delayed puberty",
            "Anosmia (no sense of smell)",
            "Underdeveloped testes",
            "Infertility",
            "No family history of delayed puberty",
          ],
          labResults: "Testosterone: low, FSH and LH: low, MRI: absent olfactory bulbs",
          correctAnswer: "Kallmann Syndrome",
          options: [
            "Kallmann Syndrome",
            "Klinefelter Syndrome",
            "Pituitary Tumor",
            "Constitutional Delay",
          ],
          explanation:
            "Hypogonadotropic hypogonadism with anosmia and absent olfactory bulbs is diagnostic of Kallmann syndrome.",
        },
        {
          id: "endo-med-15",
          patientInfo: "8-year-old girl",
          symptoms: [
            "Breast development at age 6",
            "Pubic hair growth",
            "Growth spurt",
            "Body odor",
            "Emotional changes",
          ],
          labResults: "LH and FSH: pubertal levels, Estradiol: elevated, Bone age: advanced, MRI brain: normal",
          correctAnswer: "Central Precocious Puberty",
          options: [
            "Central Precocious Puberty",
            "Peripheral Precocious Puberty",
            "Premature Thelarche",
            "Congenital Adrenal Hyperplasia",
          ],
          explanation:
            "Secondary sexual characteristics before age 8 with pubertal gonadotropin levels indicates central (GnRH-dependent) precocious puberty.",
        },
        {
          id: "endo-med-16",
          patientInfo: "45-year-old female",
          symptoms: [
            "Fatigue",
            "Cold intolerance",
            "Weight gain",
            "Bradycardia",
            "Taking amiodarone for arrhythmia",
          ],
          labResults: "TSH: elevated, Free T4: low, Anti-TPO: negative",
          correctAnswer: "Amiodarone-Induced Hypothyroidism",
          options: [
            "Amiodarone-Induced Hypothyroidism",
            "Hashimoto's Thyroiditis",
            "Subacute Thyroiditis",
            "Central Hypothyroidism",
          ],
          explanation:
            "Hypothyroidism developing while on amiodarone (iodine-rich drug) indicates amiodarone-induced thyroid dysfunction.",
        },
        {
          id: "endo-med-17",
          patientInfo: "55-year-old male",
          symptoms: [
            "Bone pain",
            "Pathological fracture",
            "Hypercalcemia",
            "Weight loss",
            "Known lung cancer",
          ],
          labResults: "Calcium: elevated, PTH: suppressed, PTHrP: elevated",
          correctAnswer: "Hypercalcemia of Malignancy",
          options: [
            "Hypercalcemia of Malignancy",
            "Primary Hyperparathyroidism",
            "Sarcoidosis",
            "Vitamin D Toxicity",
          ],
          explanation:
            "Hypercalcemia with suppressed PTH and elevated PTHrP in cancer patient indicates humoral hypercalcemia of malignancy.",
        },
        {
          id: "endo-med-18",
          patientInfo: "35-year-old female",
          symptoms: [
            "Recurrent kidney stones",
            "Peptic ulcers",
            "Hypercalcemia",
            "Family history of similar problems",
            "Pituitary tumor found incidentally",
          ],
          labResults: "Calcium: elevated, PTH: elevated, Gastrin: elevated, Genetic testing: MEN1 mutation",
          correctAnswer: "Multiple Endocrine Neoplasia Type 1",
          options: [
            "Multiple Endocrine Neoplasia Type 1",
            "Sporadic Hyperparathyroidism",
            "Zollinger-Ellison Syndrome alone",
            "MEN Type 2",
          ],
          explanation:
            "Combination of hyperparathyroidism, pituitary tumor, and elevated gastrin (ZES) with MEN1 mutation confirms MEN1 (3 P's: Parathyroid, Pituitary, Pancreas).",
        },
        {
          id: "endo-med-19",
          patientInfo: "40-year-old female",
          symptoms: [
            "Hypoglycemic episodes",
            "Symptoms during fasting",
            "Weight gain",
            "Confusion and sweating",
            "Relief with eating",
          ],
          labResults: "During symptoms: Glucose 40 mg/dL, Insulin elevated, C-peptide elevated, Sulfonylurea screen negative",
          correctAnswer: "Insulinoma",
          options: [
            "Insulinoma",
            "Sulfonylurea Overdose",
            "Factitious Hypoglycemia",
            "Adrenal Insufficiency",
          ],
          explanation:
            "Whipple's triad with elevated insulin and C-peptide during hypoglycemia with negative drug screen suggests insulinoma.",
        },
        {
          id: "endo-med-20",
          patientInfo: "50-year-old male",
          symptoms: [
            "Recurrent severe watery diarrhea",
            "Dehydration",
            "Hypokalemia",
            "Flushing",
            "Weight loss",
          ],
          labResults: "VIP: markedly elevated, CT abdomen: pancreatic mass",
          correctAnswer: "VIPoma",
          options: [
            "VIPoma",
            "Carcinoid Syndrome",
            "Gastrinoma",
            "Glucagonoma",
          ],
          explanation:
            "Watery diarrhea, hypokalemia, achlorhydria (WDHA syndrome) with elevated VIP and pancreatic tumor indicates VIPoma.",
        },
      ],
      hard: [
        {
          id: "endo-hard-1",
          patientInfo: "48-year-old male",
          symptoms: [
            "Severe headaches",
            "Visual field defects (tunnel vision)",
            "Enlarging hands and feet",
            "Coarse facial features",
            "Deep voice",
          ],
          labResults: "IGF-1: markedly elevated, MRI: pituitary mass",
          correctAnswer: "Acromegaly",
          options: [
            "Acromegaly",
            "Gigantism",
            "Hypothyroidism",
            "Cushing's Disease",
          ],
          explanation:
            "Progressive acral enlargement, coarse facial features, and bitemporal hemianopsia with elevated IGF-1 and pituitary mass indicates acromegaly (excess growth hormone in adults). The pituitary mass is causing visual field defects by compressing the optic chiasm.",
        },
        {
          id: "endo-hard-2",
          patientInfo: "35-year-old female",
          symptoms: [
            "Severe fatigue and weakness",
            "Weight loss and decreased appetite",
            "Dizziness when standing",
            "Darkening of skin (especially creases)",
            "Craving for salty foods",
          ],
          labResults: "Cortisol: very low, ACTH: markedly elevated, sodium: low, potassium: high",
          correctAnswer: "Primary Adrenal Insufficiency (Addison's Disease)",
          options: [
            "Primary Adrenal Insufficiency (Addison's Disease)",
            "Secondary Adrenal Insufficiency",
            "Hypothyroidism",
            "Chronic Fatigue Syndrome",
          ],
          explanation:
            "Low cortisol with elevated ACTH (primary failure), hyperpigmentation (from ACTH/MSH), salt craving, hypotension, and electrolyte abnormalities are classic for Addison's disease (primary adrenal insufficiency).",
        },
        {
          id: "endo-hard-3",
          patientInfo: "50-year-old male",
          symptoms: [
            "Decreased libido and erectile dysfunction",
            "Fatigue and decreased muscle mass",
            "Galactorrhea (milk production)",
            "Headaches",
            "Peripheral vision loss",
          ],
          labResults: "Prolactin: markedly elevated (250 ng/mL), testosterone: low, MRI: 2cm pituitary mass",
          correctAnswer: "Prolactinoma",
          options: [
            "Prolactinoma",
            "Non-functioning Pituitary Adenoma",
            "Primary Hypogonadism",
            "Craniopharyngioma",
          ],
          explanation:
            "Very high prolactin with a pituitary macroadenoma causing mass effects (headache, visual field defects) and hypogonadism (low testosterone, decreased libido) is diagnostic of prolactinoma, the most common pituitary tumor.",
        },
        {
          id: "endo-hard-4",
          patientInfo: "42-year-old female with Type 1 Diabetes",
          symptoms: [
            "Confusion and altered consciousness",
            "Rapid deep breathing (Kussmaul respiration)",
            "Fruity breath odor",
            "Abdominal pain and vomiting",
            "Recent illness with fever",
          ],
          labResults: "Glucose: 450 mg/dL, pH: 7.1, bicarbonate: 10, anion gap: 24, ketones: large",
          correctAnswer: "Diabetic Ketoacidosis (DKA)",
          options: [
            "Diabetic Ketoacidosis (DKA)",
            "Hyperosmolar Hyperglycemic State",
            "Lactic Acidosis",
            "Alcoholic Ketoacidosis",
          ],
          explanation:
            "Type 1 diabetic with hyperglycemia, metabolic acidosis (low pH, low bicarbonate), high anion gap, and positive ketones precipitated by infection is classic DKA. Kussmaul breathing is compensatory hyperventilation.",
        },
        {
          id: "endo-hard-5",
          patientInfo: "35-year-old female",
          symptoms: [
            "High fever and severe agitation",
            "Tachycardia and palpitations",
            "Diarrhea and vomiting",
            "Confusion and delirium",
            "History of Graves' disease, stopped meds",
          ],
          labResults: "TSH: suppressed, Free T4: markedly elevated, atrial fibrillation on ECG",
          correctAnswer: "Thyroid Storm",
          options: [
            "Thyroid Storm",
            "Sepsis",
            "Pheochromocytoma",
            "Serotonin Syndrome",
          ],
          explanation:
            "Thyroid storm is life-threatening thyrotoxicosis with fever, tachycardia, GI symptoms, and CNS changes, often triggered by stopping antithyroid therapy.",
        },
        {
          id: "endo-hard-6",
          patientInfo: "52-year-old male",
          symptoms: [
            "Sudden severe headache",
            "Visual field loss",
            "Nausea and vomiting",
            "Ophthalmoplegia",
            "History of pituitary adenoma",
          ],
          labResults: "MRI: hemorrhage within pituitary mass, cortisol: low",
          correctAnswer: "Pituitary Apoplexy",
          options: [
            "Pituitary Apoplexy",
            "Subarachnoid Hemorrhage",
            "Meningitis",
            "Temporal Arteritis",
          ],
          explanation:
            "Acute headache, visual deficits, and ophthalmoplegia with hemorrhage in a pituitary adenoma indicate pituitary apoplexy.",
        },
        {
          id: "endo-hard-7",
          patientInfo: "35-year-old male",
          symptoms: [
            "Medullary thyroid cancer",
            "Pheochromocytoma",
            "Tall marfanoid habitus",
            "Mucosal neuromas on tongue",
            "Family history of thyroid cancer",
          ],
          labResults: "Calcitonin: elevated, Metanephrines: elevated, Genetic testing: RET mutation (M918T)",
          correctAnswer: "Multiple Endocrine Neoplasia Type 2B",
          options: [
            "Multiple Endocrine Neoplasia Type 2B",
            "Multiple Endocrine Neoplasia Type 2A",
            "Multiple Endocrine Neoplasia Type 1",
            "Sporadic Pheochromocytoma",
          ],
          explanation:
            "MTC, pheochromocytoma, marfanoid habitus, and mucosal neuromas with RET mutation confirm MEN2B (most aggressive form).",
        },
        {
          id: "endo-hard-8",
          patientInfo: "28-year-old female",
          symptoms: [
            "Ambiguous genitalia noted at birth",
            "Salt-wasting crisis as neonate",
            "Now with irregular periods",
            "Hirsutism",
            "Short stature",
          ],
          labResults: "17-hydroxyprogesterone: markedly elevated, Cortisol: low, ACTH: elevated, Testosterone: elevated",
          correctAnswer: "Congenital Adrenal Hyperplasia (21-hydroxylase deficiency)",
          options: [
            "Congenital Adrenal Hyperplasia (21-hydroxylase deficiency)",
            "Polycystic Ovary Syndrome",
            "Adrenal Tumor",
            "Cushing Syndrome",
          ],
          explanation:
            "Salt-wasting, elevated 17-OHP, low cortisol, and hyperandrogenism indicate classic 21-hydroxylase deficiency CAH.",
        },
        {
          id: "endo-hard-9",
          patientInfo: "45-year-old female",
          symptoms: [
            "Recurrent episodes of flushing",
            "Watery diarrhea",
            "Wheezing",
            "Right-sided heart murmur",
            "Facial telangiectasias",
          ],
          labResults: "24-hour urine 5-HIAA: markedly elevated, CT: liver metastases, Echo: tricuspid regurgitation",
          correctAnswer: "Carcinoid Syndrome",
          options: [
            "Carcinoid Syndrome",
            "Pheochromocytoma",
            "Mastocytosis",
            "VIPoma",
          ],
          explanation:
            "Flushing, diarrhea, wheezing, right heart disease (carcinoid heart), elevated 5-HIAA, and liver mets indicate carcinoid syndrome.",
        },
        {
          id: "endo-hard-10",
          patientInfo: "55-year-old male",
          symptoms: [
            "Necrolytic migratory erythema (skin rash)",
            "Weight loss",
            "Diabetes mellitus (new onset)",
            "DVT",
            "Glossitis and angular stomatitis",
          ],
          labResults: "Glucagon: markedly elevated, CT abdomen: pancreatic mass",
          correctAnswer: "Glucagonoma",
          options: [
            "Glucagonoma",
            "VIPoma",
            "Insulinoma",
            "Gastrinoma",
          ],
          explanation:
            "4D syndrome (Dermatitis, Diabetes, DVT, Depression) with necrolytic migratory erythema and elevated glucagon indicates glucagonoma.",
        },
        {
          id: "endo-hard-11",
          patientInfo: "40-year-old male",
          symptoms: [
            "Severe peptic ulcer disease",
            "Ulcers in unusual locations (jejunum)",
            "Refractory to standard therapy",
            "Diarrhea",
            "Hypercalcemia",
          ],
          labResults: "Gastrin: markedly elevated (>1000 pg/mL), Secretin stimulation test: paradoxical rise in gastrin, CT: pancreatic mass",
          correctAnswer: "Zollinger-Ellison Syndrome (Gastrinoma)",
          options: [
            "Zollinger-Ellison Syndrome (Gastrinoma)",
            "H. pylori Infection",
            "NSAID-Induced Ulcers",
            "Stress Ulcers",
          ],
          explanation:
            "Refractory peptic ulcers, markedly elevated gastrin with paradoxical secretin response, and pancreatic tumor indicate gastrinoma (ZES).",
        },
        {
          id: "endo-hard-12",
          patientInfo: "60-year-old female",
          symptoms: [
            "Diabetes insipidus",
            "Exophthalmos",
            "Skin lesions on shins",
            "Lytic bone lesions",
            "Pulmonary nodules",
          ],
          labResults: "Biopsy of skin lesion: Langerhans cell infiltration, CD1a positive",
          correctAnswer: "Langerhans Cell Histiocytosis",
          options: [
            "Langerhans Cell Histiocytosis",
            "Sarcoidosis",
            "Metastatic Cancer",
            "Multiple Myeloma",
          ],
          explanation:
            "Multi-system disease with DI (pituitary involvement), skin lesions, bone lesions, and CD1a+ Langerhans cells confirms LCH.",
        },
        {
          id: "endo-hard-13",
          patientInfo: "50-year-old male",
          symptoms: [
            "Severe hypertension with hypokalemia",
            "Metabolic alkalosis",
            "Young patient with resistant hypertension",
            "Adrenal incidentaloma found",
            "No edema",
          ],
          labResults: "Aldosterone: elevated, Renin: suppressed, ARR >30, CT: unilateral adrenal adenoma, AVS: lateralizes to left adrenal",
          correctAnswer: "Aldosterone-Producing Adenoma (Conn Syndrome)",
          options: [
            "Aldosterone-Producing Adenoma (Conn Syndrome)",
            "Bilateral Adrenal Hyperplasia",
            "Glucocorticoid-Remediable Aldosteronism",
            "Renovascular Hypertension",
          ],
          explanation:
            "Primary aldosteronism with unilateral adenoma and lateralization on AVS indicates aldosterone-producing adenoma (curable surgically).",
        },
        {
          id: "endo-hard-14",
          patientInfo: "25-year-old male",
          symptoms: [
            "Bilateral gynecomastia",
            "Testicular mass",
            "Elevated hCG",
            "Hyperthyroidism symptoms",
            "No family history",
          ],
          labResults: "Beta-hCG: markedly elevated, TSH: suppressed, Free T4: elevated, Testicular ultrasound: testicular mass",
          correctAnswer: "hCG-Secreting Germ Cell Tumor",
          options: [
            "hCG-Secreting Germ Cell Tumor",
            "Graves' Disease",
            "Klinefelter Syndrome",
            "Prolactinoma",
          ],
          explanation:
            "Gynecomastia, testicular mass, elevated hCG (which has TSH-like activity causing hyperthyroidism) indicates hCG-secreting germ cell tumor.",
        },
        {
          id: "endo-hard-15",
          patientInfo: "35-year-old female",
          symptoms: [
            "Severe osteoporosis with fractures",
            "Hypercalciuria",
            "Normal serum calcium",
            "Low PTH",
            "No vitamin D excess",
          ],
          labResults: "Serum calcium: normal, Urine calcium: markedly elevated, PTH: suppressed, 25-OH vitamin D: normal",
          correctAnswer: "Idiopathic Hypercalciuria",
          options: [
            "Idiopathic Hypercalciuria",
            "Primary Hyperparathyroidism",
            "Vitamin D Toxicity",
            "Sarcoidosis",
          ],
          explanation:
            "Nephrolithiasis/osteoporosis with hypercalciuria but normal serum calcium and suppressed PTH indicates idiopathic hypercalciuria.",
        },
        {
          id: "endo-hard-16",
          patientInfo: "45-year-old male",
          symptoms: [
            "Episodes of hypertension, headache, sweating",
            "Abdominal paraganglioma",
            "Family history of similar tumors",
            "Carotid body tumor also found",
            "Multiple tumors",
          ],
          labResults: "Metanephrines: elevated, SDHB mutation: positive",
          correctAnswer: "Hereditary Paraganglioma Syndrome",
          options: [
            "Hereditary Paraganglioma Syndrome",
            "Sporadic Pheochromocytoma",
            "MEN Type 2",
            "Von Hippel-Lindau Disease",
          ],
          explanation:
            "Multiple paragangliomas with SDH mutation indicates hereditary paraganglioma-pheochromocytoma syndrome.",
        },
        {
          id: "endo-hard-17",
          patientInfo: "30-year-old female",
          symptoms: [
            "Recurrent hypoglycemia",
            "Symptoms after meals (postprandial)",
            "Prior gastric bypass surgery",
            "Neuroglycopenic symptoms",
            "No diabetes medications",
          ],
          labResults: "During hypoglycemia: Insulin elevated, C-peptide elevated, Proinsulin elevated, No sulfonylurea detected",
          correctAnswer: "Post-Bariatric Hypoglycemia (Dumping Syndrome)",
          options: [
            "Post-Bariatric Hypoglycemia (Dumping Syndrome)",
            "Insulinoma",
            "Factitious Hypoglycemia",
            "Reactive Hypoglycemia",
          ],
          explanation:
            "Postprandial hypoglycemia with elevated endogenous insulin after gastric bypass indicates post-bariatric hyperinsulinemic hypoglycemia.",
        },
        {
          id: "endo-hard-18",
          patientInfo: "55-year-old female",
          symptoms: [
            "Resistant hypertension",
            "Hypokalemia",
            "Elevated aldosterone and renin",
            "Abdominal bruit",
            "Flash pulmonary edema",
          ],
          labResults: "Aldosterone: elevated, Renin: elevated, Renal artery duplex: >60% stenosis of right renal artery",
          correctAnswer: "Renovascular Hypertension",
          options: [
            "Renovascular Hypertension",
            "Primary Hyperaldosteronism",
            "Pheochromocytoma",
            "Essential Hypertension",
          ],
          explanation:
            "Secondary aldosteronism (high renin, high aldosterone) with renal artery stenosis indicates renovascular hypertension.",
        },
        {
          id: "endo-hard-19",
          patientInfo: "40-year-old male",
          symptoms: [
            "Short stature",
            "No sense of smell",
            "Micropenis",
            "Undescended testes (history of)",
            "Mirror movements (synkinesis)",
          ],
          labResults: "Testosterone: very low, LH/FSH: very low, MRI: absent olfactory bulbs, Genetic testing: KAL1 mutation",
          correctAnswer: "X-linked Kallmann Syndrome",
          options: [
            "X-linked Kallmann Syndrome",
            "Klinefelter Syndrome",
            "Pituitary Tumor",
            "Prader-Willi Syndrome",
          ],
          explanation:
            "Hypogonadotropic hypogonadism with anosmia, mirror movements, and KAL1 mutation indicates X-linked Kallmann syndrome.",
        },
        {
          id: "endo-hard-20",
          patientInfo: "28-year-old female",
          symptoms: [
            "Thyroid storm after iodinated contrast",
            "History of multinodular goiter",
            "Palpitations and fever",
            "Altered mental status",
            "Recent CT scan with contrast",
          ],
          labResults: "TSH: undetectable, Free T4 and T3: markedly elevated, Burch-Wartofsky score: >45",
          correctAnswer: "Iodine-Induced Thyrotoxicosis (Jod-Basedow)",
          options: [
            "Iodine-Induced Thyrotoxicosis (Jod-Basedow)",
            "Graves' Disease",
            "Thyroid Storm from Graves'",
            "Subacute Thyroiditis",
          ],
          explanation:
            "Thyrotoxicosis/storm after iodine load in patient with pre-existing goiter indicates Jod-Basedow phenomenon (iodine-induced hyperthyroidism).",
        },
      ],
    },
    general: {
      easy: [
        {
          id: "gen-easy-1",
          patientInfo: "45-year-old male",
          symptoms: [
            "Severe right upper quadrant pain",
            "Pain radiating to right shoulder",
            "Nausea and vomiting",
            "Pain worse after eating fatty meals",
            "Fever",
          ],
          labResults: "Ultrasound: gallstones, thickened gallbladder wall",
          correctAnswer: "Acute Cholecystitis",
          options: [
            "Acute Cholecystitis",
            "Peptic Ulcer Disease",
            "Hepatitis",
            "Pancreatitis",
          ],
          explanation:
            "Right upper quadrant pain radiating to the shoulder after fatty meals, with ultrasound showing gallstones and inflammation, confirms acute cholecystitis (gallbladder inflammation).",
        },
        {
          id: "gen-easy-2",
          patientInfo: "25-year-old male",
          symptoms: [
            "Right lower quadrant abdominal pain",
            "Pain started around navel then moved",
            "Loss of appetite",
            "Nausea and vomiting",
            "Low-grade fever",
          ],
          labResults: "WBC: elevated (14,000), CT abdomen: inflamed appendix with periappendiceal fat stranding",
          correctAnswer: "Acute Appendicitis",
          options: [
            "Acute Appendicitis",
            "Gastroenteritis",
            "Kidney Stone",
            "Crohn's Disease",
          ],
          explanation:
            "Classic migration of periumbilical pain to RLQ (McBurney's point), anorexia, nausea, low-grade fever, and CT showing appendiceal inflammation confirms acute appendicitis.",
        },
        {
          id: "gen-easy-3",
          patientInfo: "30-year-old female",
          symptoms: [
            "Burning pain with urination",
            "Frequent urge to urinate",
            "Lower abdominal discomfort",
            "Cloudy, strong-smelling urine",
            "No fever or back pain",
          ],
          labResults: "Urinalysis: positive nitrites, positive leukocyte esterase, WBC in urine",
          correctAnswer: "Urinary Tract Infection (Cystitis)",
          options: [
            "Urinary Tract Infection (Cystitis)",
            "Pyelonephritis",
            "Kidney Stone",
            "Interstitial Cystitis",
          ],
          explanation:
            "Dysuria, frequency, and urgency without systemic symptoms (no fever) and positive urinalysis indicate uncomplicated cystitis (lower UTI). Absence of fever and flank pain rules out pyelonephritis.",
        },
        {
          id: "gen-easy-4",
          patientInfo: "60-year-old male smoker",
          symptoms: [
            "Productive cough with green sputum",
            "Shortness of breath",
            "Wheezing",
            "Low-grade fever",
            "History of chronic bronchitis",
          ],
          labResults: "Chest X-ray: hyperinflation, no consolidation, SpO2: 88%",
          correctAnswer: "COPD Exacerbation",
          options: [
            "COPD Exacerbation",
            "Pneumonia",
            "Pulmonary Embolism",
            "Lung Cancer",
          ],
          explanation:
            "Known COPD patient with increased dyspnea, purulent sputum, and hypoxia without pneumonic consolidation indicates acute COPD exacerbation, often triggered by infection.",
        },
        {
          id: "gen-easy-5",
          patientInfo: "21-year-old college student",
          symptoms: [
            "Watery diarrhea for 2 days",
            "Abdominal cramping",
            "Nausea and vomiting",
            "Low-grade fever",
            "Roommates with similar symptoms",
          ],
          labResults: "Basic labs: mild dehydration, no leukocytosis",
          correctAnswer: "Acute Gastroenteritis",
          options: [
            "Acute Gastroenteritis",
            "Appendicitis",
            "Inflammatory Bowel Disease",
            "Pancreatitis",
          ],
          explanation:
            "Short duration diarrhea and vomiting with sick contacts suggests viral gastroenteritis. Supportive care and hydration are typical.",
        },
        {
          id: "gen-easy-6",
          patientInfo: "33-year-old female",
          symptoms: [
            "Facial pressure and pain",
            "Nasal congestion with purulent discharge",
            "Symptoms for 10 days",
            "Low-grade fever",
            "Worse when bending forward",
          ],
          labResults: "Clinical diagnosis, no imaging needed",
          correctAnswer: "Acute Bacterial Sinusitis",
          options: [
            "Acute Bacterial Sinusitis",
            "Allergic Rhinitis",
            "Migraine",
            "Influenza",
          ],
          explanation:
            "Persistent purulent nasal discharge and facial pain beyond 10 days with worsening suggests acute bacterial sinusitis.",
        },
        {
          id: "gen-easy-7",
          patientInfo: "68-year-old male",
          symptoms: [
            "Productive cough with green sputum",
            "Fever and chills",
            "Shortness of breath",
            "Chest pain with breathing",
            "Confusion",
          ],
          labResults: "Chest X-ray: right lower lobe consolidation, WBC: elevated, Procalcitonin: elevated",
          correctAnswer: "Community-Acquired Pneumonia",
          options: [
            "Community-Acquired Pneumonia",
            "Acute Bronchitis",
            "COPD Exacerbation",
            "Pulmonary Embolism",
          ],
          explanation:
            "Fever, productive cough, and lobar consolidation on chest X-ray with elevated inflammatory markers indicate bacterial pneumonia.",
        },
        {
          id: "gen-easy-8",
          patientInfo: "25-year-old female",
          symptoms: [
            "Burning with urination",
            "Urinary frequency and urgency",
            "Suprapubic discomfort",
            "No fever or back pain",
            "Sexually active",
          ],
          labResults: "Urinalysis: positive leukocyte esterase, nitrites positive, WBC in urine",
          correctAnswer: "Urinary Tract Infection (Cystitis)",
          options: [
            "Urinary Tract Infection (Cystitis)",
            "Pyelonephritis",
            "Interstitial Cystitis",
            "Sexually Transmitted Infection",
          ],
          explanation:
            "Dysuria, frequency, urgency with positive urinalysis and no systemic symptoms indicates uncomplicated cystitis.",
        },
        {
          id: "gen-easy-9",
          patientInfo: "40-year-old male",
          symptoms: [
            "Severe sore throat",
            "Fever",
            "Swollen tender lymph nodes in neck",
            "No cough",
            "Tonsillar exudates",
          ],
          labResults: "Rapid strep test: positive",
          correctAnswer: "Streptococcal Pharyngitis",
          options: [
            "Streptococcal Pharyngitis",
            "Viral Pharyngitis",
            "Infectious Mononucleosis",
            "Peritonsillar Abscess",
          ],
          explanation:
            "Centor criteria (fever, tonsillar exudates, tender anterior cervical nodes, no cough) with positive strep test confirms strep pharyngitis.",
        },
        {
          id: "gen-easy-10",
          patientInfo: "55-year-old male",
          symptoms: [
            "Burning epigastric pain",
            "Pain worse when hungry",
            "Relief with food or antacids",
            "Night-time awakening",
            "History of NSAID use",
          ],
          labResults: "H. pylori stool antigen: positive",
          correctAnswer: "Peptic Ulcer Disease",
          options: [
            "Peptic Ulcer Disease",
            "GERD",
            "Gastritis",
            "Functional Dyspepsia",
          ],
          explanation:
            "Epigastric pain relieved by food, worsened by hunger, with positive H. pylori and NSAID use suggests peptic ulcer disease.",
        },
        {
          id: "gen-easy-11",
          patientInfo: "35-year-old female",
          symptoms: [
            "Heartburn after meals",
            "Regurgitation of food",
            "Worse when lying down",
            "Chronic cough at night",
            "Sour taste in mouth",
          ],
          labResults: "Clinical diagnosis, upper endoscopy: esophagitis",
          correctAnswer: "Gastroesophageal Reflux Disease (GERD)",
          options: [
            "Gastroesophageal Reflux Disease (GERD)",
            "Peptic Ulcer Disease",
            "Esophageal Cancer",
            "Achalasia",
          ],
          explanation:
            "Heartburn, regurgitation, and nocturnal cough with esophagitis on endoscopy confirm GERD.",
        },
        {
          id: "gen-easy-12",
          patientInfo: "60-year-old male",
          symptoms: [
            "Chronic cough with sputum production",
            "Shortness of breath on exertion",
            "Wheezing",
            "History of 40 pack-years smoking",
            "Recurrent respiratory infections",
          ],
          labResults: "Spirometry: FEV1/FVC ratio <0.70, post-bronchodilator FEV1 55% predicted",
          correctAnswer: "Chronic Obstructive Pulmonary Disease (COPD)",
          options: [
            "Chronic Obstructive Pulmonary Disease (COPD)",
            "Asthma",
            "Bronchiectasis",
            "Interstitial Lung Disease",
          ],
          explanation:
            "Chronic cough, dyspnea, smoking history, and fixed airflow obstruction on spirometry (FEV1/FVC <0.70) indicate COPD.",
        },
        {
          id: "gen-easy-13",
          patientInfo: "22-year-old male",
          symptoms: [
            "Episodic wheezing",
            "Shortness of breath",
            "Chest tightness",
            "Symptoms worse at night and with exercise",
            "History of allergies",
          ],
          labResults: "Spirometry: FEV1/FVC reduced, significant improvement with bronchodilator (>12%)",
          correctAnswer: "Asthma",
          options: [
            "Asthma",
            "COPD",
            "Vocal Cord Dysfunction",
            "Heart Failure",
          ],
          explanation:
            "Episodic symptoms, allergic history, and reversible airflow obstruction on spirometry are characteristic of asthma.",
        },
        {
          id: "gen-easy-14",
          patientInfo: "50-year-old female",
          symptoms: [
            "Joint pain and stiffness in hands",
            "Morning stiffness lasting >1 hour",
            "Symmetric involvement",
            "Swelling of MCP and PIP joints",
            "Fatigue",
          ],
          labResults: "RF: positive, Anti-CCP: positive, ESR and CRP: elevated",
          correctAnswer: "Rheumatoid Arthritis",
          options: [
            "Rheumatoid Arthritis",
            "Osteoarthritis",
            "Psoriatic Arthritis",
            "Gout",
          ],
          explanation:
            "Symmetric small joint involvement, prolonged morning stiffness, positive RF and anti-CCP antibodies indicate rheumatoid arthritis.",
        },
        {
          id: "gen-easy-15",
          patientInfo: "70-year-old male",
          symptoms: [
            "Knee pain worse with activity",
            "Brief morning stiffness (<30 min)",
            "Crepitus on exam",
            "Bony enlargement",
            "No warmth or redness",
          ],
          labResults: "X-ray: joint space narrowing, osteophytes, subchondral sclerosis",
          correctAnswer: "Osteoarthritis",
          options: [
            "Osteoarthritis",
            "Rheumatoid Arthritis",
            "Gout",
            "Septic Arthritis",
          ],
          explanation:
            "Mechanical joint pain, brief stiffness, crepitus, and classic X-ray findings indicate osteoarthritis.",
        },
        {
          id: "gen-easy-16",
          patientInfo: "45-year-old male",
          symptoms: [
            "Sudden severe pain in big toe",
            "Red, hot, swollen joint",
            "Unable to bear weight",
            "Woke up from sleep with pain",
            "History of alcohol and red meat consumption",
          ],
          labResults: "Synovial fluid: negatively birefringent needle-shaped crystals, Uric acid: elevated",
          correctAnswer: "Gout",
          options: [
            "Gout",
            "Septic Arthritis",
            "Pseudogout",
            "Cellulitis",
          ],
          explanation:
            "Acute monoarthritis of first MTP with negatively birefringent crystals (monosodium urate) confirms gout.",
        },
        {
          id: "gen-easy-17",
          patientInfo: "30-year-old female",
          symptoms: [
            "Fatigue",
            "Pallor",
            "Shortness of breath on exertion",
            "Heavy menstrual periods",
            "Pica (craving ice)",
          ],
          labResults: "Hemoglobin: 9 g/dL, MCV: low, Ferritin: very low, Iron: low, TIBC: elevated",
          correctAnswer: "Iron Deficiency Anemia",
          options: [
            "Iron Deficiency Anemia",
            "Anemia of Chronic Disease",
            "Thalassemia",
            "Vitamin B12 Deficiency",
          ],
          explanation:
            "Microcytic anemia with low ferritin, low iron, and elevated TIBC in a woman with menorrhagia indicates iron deficiency anemia.",
        },
        {
          id: "gen-easy-18",
          patientInfo: "65-year-old female",
          symptoms: [
            "Fatigue and weakness",
            "Numbness and tingling in feet",
            "Unsteady gait",
            "Glossitis (smooth tongue)",
            "Strict vegan diet for 10 years",
          ],
          labResults: "Hemoglobin: 10 g/dL, MCV: elevated, Vitamin B12: very low, Methylmalonic acid: elevated",
          correctAnswer: "Vitamin B12 Deficiency",
          options: [
            "Vitamin B12 Deficiency",
            "Folate Deficiency",
            "Iron Deficiency Anemia",
            "Hypothyroidism",
          ],
          explanation:
            "Macrocytic anemia with neurological symptoms, glossitis, low B12, and elevated MMA in a vegan indicates B12 deficiency.",
        },
        {
          id: "gen-easy-19",
          patientInfo: "48-year-old male",
          symptoms: [
            "Yellowing of skin and eyes",
            "Dark urine",
            "Pale stools",
            "Itching",
            "Right upper quadrant discomfort",
          ],
          labResults: "Bilirubin: elevated (direct > indirect), ALP: markedly elevated, GGT: elevated, Ultrasound: dilated bile ducts",
          correctAnswer: "Obstructive Jaundice",
          options: [
            "Obstructive Jaundice",
            "Hepatocellular Jaundice",
            "Hemolytic Jaundice",
            "Gilbert Syndrome",
          ],
          explanation:
            "Elevated direct bilirubin, ALP, pale stools, dark urine, and dilated bile ducts indicate obstructive (cholestatic) jaundice.",
        },
        {
          id: "gen-easy-20",
          patientInfo: "55-year-old male",
          symptoms: [
            "Chronic fatigue",
            "Right upper quadrant discomfort",
            "History of IV drug use years ago",
            "Mild jaundice",
            "Spider angiomas on skin",
          ],
          labResults: "ALT and AST: elevated, Hepatitis C antibody: positive, HCV RNA: detected",
          correctAnswer: "Chronic Hepatitis C",
          options: [
            "Chronic Hepatitis C",
            "Hepatitis B",
            "Alcoholic Liver Disease",
            "Non-Alcoholic Fatty Liver Disease",
          ],
          explanation:
            "History of IV drug use, elevated transaminases, positive HCV antibody, and detectable viral RNA confirm chronic hepatitis C.",
        },
      ],
      medium: [
        {
          id: "gen-med-1",
          patientInfo: "55-year-old female",
          symptoms: [
            "Persistent cough for 6 weeks",
            "Night sweats",
            "Unintentional weight loss of 10 lbs",
            "Low-grade fever",
            "Recent immigrant from endemic area",
          ],
          labResults:
            "Chest X-ray: upper lobe infiltrate with cavitation, AFB smear: positive",
          correctAnswer: "Pulmonary Tuberculosis",
          options: [
            "Pulmonary Tuberculosis",
            "Pneumonia",
            "Lung Cancer",
            "Bronchitis",
          ],
          explanation:
            "Constitutional symptoms (fever, night sweats, weight loss), chronic cough, upper lobe cavitary lesion, and positive acid-fast bacilli smear are diagnostic of active pulmonary tuberculosis.",
        },
        {
          id: "gen-med-2",
          patientInfo: "48-year-old female",
          symptoms: [
            "Joint pain and stiffness in hands",
            "Symmetric involvement of small joints",
            "Morning stiffness lasting over 1 hour",
            "Fatigue and low-grade fever",
            "Subcutaneous nodules near elbows",
          ],
          labResults: "RF: positive, Anti-CCP: positive, ESR: elevated, X-ray: periarticular erosions",
          correctAnswer: "Rheumatoid Arthritis",
          options: [
            "Rheumatoid Arthritis",
            "Osteoarthritis",
            "Gout",
            "Systemic Lupus Erythematosus",
          ],
          explanation:
            "Symmetric inflammatory polyarthritis of small joints with prolonged morning stiffness, positive RF and anti-CCP, and erosive changes on X-ray are diagnostic of rheumatoid arthritis.",
        },
        {
          id: "gen-med-3",
          patientInfo: "35-year-old female",
          symptoms: [
            "Butterfly-shaped rash on face",
            "Joint pain in multiple areas",
            "Fatigue and low-grade fevers",
            "Hair loss",
            "Sensitivity to sunlight",
          ],
          labResults: "ANA: positive (1:640), Anti-dsDNA: positive, low complement levels (C3, C4), proteinuria",
          correctAnswer: "Systemic Lupus Erythematosus (SLE)",
          options: [
            "Systemic Lupus Erythematosus (SLE)",
            "Rheumatoid Arthritis",
            "Dermatomyositis",
            "Rosacea",
          ],
          explanation:
            "Malar rash, photosensitivity, arthritis, positive ANA and anti-dsDNA, low complement, and renal involvement (proteinuria) meet multiple criteria for SLE, a systemic autoimmune disease.",
        },
        {
          id: "gen-med-4",
          patientInfo: "65-year-old male",
          symptoms: [
            "Sudden severe pain in big toe",
            "Joint is red, hot, and swollen",
            "Cannot bear weight on foot",
            "Recent large steak dinner with alcohol",
            "History of kidney disease",
          ],
          labResults: "Uric acid: elevated, Joint aspiration: negatively birefringent needle-shaped crystals",
          correctAnswer: "Acute Gout",
          options: [
            "Acute Gout",
            "Septic Arthritis",
            "Pseudogout",
            "Cellulitis",
          ],
          explanation:
            "Acute monoarticular arthritis of the first MTP joint (podagra) with elevated uric acid and negatively birefringent crystals on joint aspiration is diagnostic of acute gout. Dietary triggers and renal disease are risk factors.",
        },
        {
          id: "gen-med-5",
          patientInfo: "46-year-old female",
          symptoms: [
            "Sudden shortness of breath",
            "Pleuritic chest pain",
            "Tachycardia",
            "Mild hemoptysis",
            "Recent long flight",
          ],
          labResults: "D-dimer: elevated, CT angiography: pulmonary artery filling defect",
          correctAnswer: "Pulmonary Embolism",
          options: [
            "Pulmonary Embolism",
            "Pneumonia",
            "Pneumothorax",
            "Acute Bronchitis",
          ],
          explanation:
            "Acute dyspnea and pleuritic pain after prolonged immobilization with confirmatory CT angiography indicate pulmonary embolism.",
        },
        {
          id: "gen-med-6",
          patientInfo: "57-year-old male",
          symptoms: [
            "Fever and productive cough",
            "Pleuritic chest pain",
            "Shortness of breath",
            "Fatigue and chills",
            "Crackles at right lung base",
          ],
          labResults: "Chest X-ray: right lower lobe consolidation, WBC: 15,000/μL",
          correctAnswer: "Community-Acquired Pneumonia",
          options: [
            "Community-Acquired Pneumonia",
            "Pulmonary Embolism",
            "COPD Exacerbation",
            "Heart Failure",
          ],
          explanation:
            "Fever, productive cough, and focal consolidation on chest X-ray indicate community-acquired pneumonia.",
        },
        {
          id: "gen-med-7",
          patientInfo: "45-year-old female",
          symptoms: [
            "Chronic diarrhea with blood and mucus",
            "Abdominal cramping",
            "Urgency to defecate",
            "Weight loss",
            "Episodes lasting weeks to months",
          ],
          labResults: "Colonoscopy: continuous inflammation from rectum, Biopsy: crypt abscesses and mucosal ulceration",
          correctAnswer: "Ulcerative Colitis",
          options: [
            "Ulcerative Colitis",
            "Crohn's Disease",
            "Infectious Colitis",
            "Irritable Bowel Syndrome",
          ],
          explanation:
            "Continuous colonic inflammation starting from rectum with crypt abscesses is characteristic of ulcerative colitis.",
        },
        {
          id: "gen-med-8",
          patientInfo: "28-year-old male",
          symptoms: [
            "Abdominal pain and diarrhea",
            "Perianal fistula",
            "Weight loss",
            "Mouth ulcers",
            "Skip lesions on imaging",
          ],
          labResults: "Colonoscopy: patchy inflammation, cobblestoning, Biopsy: non-caseating granulomas",
          correctAnswer: "Crohn's Disease",
          options: [
            "Crohn's Disease",
            "Ulcerative Colitis",
            "Intestinal Tuberculosis",
            "Celiac Disease",
          ],
          explanation:
            "Skip lesions, transmural inflammation, perianal disease, and granulomas are characteristic of Crohn's disease.",
        },
        {
          id: "gen-med-9",
          patientInfo: "35-year-old female",
          symptoms: [
            "Chronic diarrhea and bloating",
            "Weight loss",
            "Iron deficiency anemia",
            "Dermatitis herpetiformis (itchy skin rash)",
            "Symptoms improve when avoiding bread",
          ],
          labResults: "Anti-tissue transglutaminase IgA: strongly positive, Duodenal biopsy: villous atrophy",
          correctAnswer: "Celiac Disease",
          options: [
            "Celiac Disease",
            "Crohn's Disease",
            "Tropical Sprue",
            "Lactose Intolerance",
          ],
          explanation:
            "Malabsorption, positive tTG-IgA, villous atrophy, and dermatitis herpetiformis confirm celiac disease.",
        },
        {
          id: "gen-med-10",
          patientInfo: "50-year-old male",
          symptoms: [
            "Flank pain radiating to groin",
            "Severe colicky pain",
            "Hematuria",
            "Nausea and vomiting",
            "Unable to find comfortable position",
          ],
          labResults: "Urinalysis: blood, CT abdomen: 6mm stone in left ureter",
          correctAnswer: "Ureteral Stone (Nephrolithiasis)",
          options: [
            "Ureteral Stone (Nephrolithiasis)",
            "Pyelonephritis",
            "Appendicitis",
            "Abdominal Aortic Aneurysm",
          ],
          explanation:
            "Colicky flank pain radiating to groin with hematuria and stone on CT confirms ureteral colic.",
        },
        {
          id: "gen-med-11",
          patientInfo: "32-year-old female",
          symptoms: [
            "High fever and flank pain",
            "Dysuria",
            "Nausea and vomiting",
            "Costovertebral angle tenderness",
            "Rigors",
          ],
          labResults: "Urinalysis: pyuria and bacteriuria, Urine culture: E. coli >100,000 CFU/mL, WBC: elevated",
          correctAnswer: "Acute Pyelonephritis",
          options: [
            "Acute Pyelonephritis",
            "Cystitis",
            "Nephrolithiasis",
            "Appendicitis",
          ],
          explanation:
            "Fever, flank pain, CVA tenderness with positive urine culture indicates ascending infection (pyelonephritis).",
        },
        {
          id: "gen-med-12",
          patientInfo: "65-year-old male",
          symptoms: [
            "Progressive dyspnea",
            "Dry cough",
            "Clubbing of fingers",
            "Fine inspiratory crackles (velcro-like)",
            "History of asbestos exposure",
          ],
          labResults: "High-resolution CT: honeycombing and traction bronchiectasis, Spirometry: restrictive pattern",
          correctAnswer: "Idiopathic Pulmonary Fibrosis",
          options: [
            "Idiopathic Pulmonary Fibrosis",
            "COPD",
            "Sarcoidosis",
            "Hypersensitivity Pneumonitis",
          ],
          explanation:
            "Progressive dyspnea, velcro crackles, clubbing, and UIP pattern on HRCT indicate idiopathic pulmonary fibrosis.",
        },
        {
          id: "gen-med-13",
          patientInfo: "40-year-old African American female",
          symptoms: [
            "Cough and dyspnea",
            "Bilateral hilar lymphadenopathy on X-ray",
            "Erythema nodosum on shins",
            "Eye redness and pain",
            "Fatigue",
          ],
          labResults: "ACE level: elevated, Biopsy: non-caseating granulomas, Calcium: elevated",
          correctAnswer: "Sarcoidosis",
          options: [
            "Sarcoidosis",
            "Tuberculosis",
            "Lymphoma",
            "Histoplasmosis",
          ],
          explanation:
            "Bilateral hilar adenopathy, erythema nodosum, uveitis, elevated ACE, and non-caseating granulomas indicate sarcoidosis.",
        },
        {
          id: "gen-med-14",
          patientInfo: "55-year-old female",
          symptoms: [
            "Butterfly-shaped facial rash",
            "Joint pain and swelling",
            "Photosensitivity",
            "Oral ulcers",
            "Fatigue",
          ],
          labResults: "ANA: strongly positive, Anti-dsDNA: positive, Low complement (C3, C4), Proteinuria",
          correctAnswer: "Systemic Lupus Erythematosus",
          options: [
            "Systemic Lupus Erythematosus",
            "Rheumatoid Arthritis",
            "Dermatomyositis",
            "Scleroderma",
          ],
          explanation:
            "Malar rash, arthritis, photosensitivity, positive ANA/anti-dsDNA, and renal involvement meet criteria for SLE.",
        },
        {
          id: "gen-med-15",
          patientInfo: "50-year-old female",
          symptoms: [
            "Tight, thickened skin on fingers",
            "Raynaud phenomenon",
            "Difficulty swallowing",
            "Shortness of breath",
            "Telangiectasias",
          ],
          labResults: "ANA: positive, Anti-Scl-70: positive, CT chest: interstitial lung disease",
          correctAnswer: "Systemic Sclerosis (Scleroderma)",
          options: [
            "Systemic Sclerosis (Scleroderma)",
            "Systemic Lupus Erythematosus",
            "Mixed Connective Tissue Disease",
            "Dermatomyositis",
          ],
          explanation:
            "Skin thickening, Raynaud's, dysphagia, ILD, and anti-Scl-70 antibody indicate diffuse systemic sclerosis.",
        },
        {
          id: "gen-med-16",
          patientInfo: "60-year-old male",
          symptoms: [
            "Chronic lower back pain",
            "Morning stiffness >30 minutes",
            "Pain improves with exercise",
            "Reduced spinal mobility",
            "History of uveitis",
          ],
          labResults: "HLA-B27: positive, X-ray: sacroiliitis and bamboo spine, CRP: elevated",
          correctAnswer: "Ankylosing Spondylitis",
          options: [
            "Ankylosing Spondylitis",
            "Mechanical Low Back Pain",
            "Degenerative Disc Disease",
            "Rheumatoid Arthritis",
          ],
          explanation:
            "Inflammatory back pain, sacroiliitis, bamboo spine, HLA-B27 positivity, and uveitis indicate ankylosing spondylitis.",
        },
        {
          id: "gen-med-17",
          patientInfo: "45-year-old male",
          symptoms: [
            "Chronic dry cough",
            "Dyspnea on exertion",
            "Works in sandblasting industry",
            "Fine inspiratory crackles",
            "20 years of occupational exposure",
          ],
          labResults: "Chest X-ray: bilateral upper lobe nodular opacities with eggshell calcifications, Spirometry: restrictive pattern",
          correctAnswer: "Silicosis",
          options: [
            "Silicosis",
            "Tuberculosis",
            "Sarcoidosis",
            "Coal Worker's Pneumoconiosis",
          ],
          explanation:
            "Occupational silica exposure with upper lobe nodules and eggshell calcification of hilar nodes indicates silicosis.",
        },
        {
          id: "gen-med-18",
          patientInfo: "70-year-old male",
          symptoms: [
            "Bone pain, especially in back",
            "Fatigue",
            "Recurrent infections",
            "Elevated creatinine",
            "Unexplained anemia",
          ],
          labResults: "Serum protein electrophoresis: M-spike, Bone marrow: >10% plasma cells, Lytic bone lesions on X-ray",
          correctAnswer: "Multiple Myeloma",
          options: [
            "Multiple Myeloma",
            "Metastatic Cancer",
            "Osteoporosis",
            "Primary Hyperparathyroidism",
          ],
          explanation:
            "CRAB criteria (Calcium elevation, Renal insufficiency, Anemia, Bone lesions) with M-spike and plasma cells indicate multiple myeloma.",
        },
        {
          id: "gen-med-19",
          patientInfo: "25-year-old male",
          symptoms: [
            "Painless lymphadenopathy",
            "Night sweats",
            "Unexplained weight loss",
            "Fatigue",
            "Itching after alcohol consumption",
          ],
          labResults: "Lymph node biopsy: Reed-Sternberg cells, CD15 and CD30 positive",
          correctAnswer: "Hodgkin Lymphoma",
          options: [
            "Hodgkin Lymphoma",
            "Non-Hodgkin Lymphoma",
            "Infectious Mononucleosis",
            "Tuberculosis",
          ],
          explanation:
            "B symptoms, painless lymphadenopathy, and Reed-Sternberg cells on biopsy confirm Hodgkin lymphoma.",
        },
        {
          id: "gen-med-20",
          patientInfo: "58-year-old male",
          symptoms: [
            "Elevated blood pressure readings",
            "Headaches",
            "Hypokalemia on routine labs",
            "Fatigue",
            "No peripheral edema",
          ],
          labResults: "Aldosterone: elevated, Renin: suppressed, Aldosterone/Renin ratio >30, CT: adrenal adenoma",
          correctAnswer: "Primary Aldosteronism",
          options: [
            "Primary Aldosteronism",
            "Essential Hypertension",
            "Renovascular Hypertension",
            "Cushing Syndrome",
          ],
          explanation:
            "Hypertension with hypokalemia, high aldosterone, suppressed renin, and adrenal adenoma indicate primary aldosteronism.",
        },
      ],
      hard: [
        {
          id: "gen-hard-1",
          patientInfo: "62-year-old male",
          symptoms: [
            "Painless jaundice",
            "Dark urine and pale stools",
            "Unexplained weight loss",
            "Itching all over body",
            "Abdominal mass palpable",
          ],
          labResults:
            "Direct bilirubin: elevated, CA 19-9: markedly elevated, CT: mass in pancreatic head",
          correctAnswer: "Pancreatic Cancer",
          options: [
            "Pancreatic Cancer",
            "Gallstones",
            "Hepatitis",
            "Pancreatitis",
          ],
          explanation:
            "Painless jaundice (Courvoisier's sign - palpable gallbladder with painless jaundice), weight loss, and mass in pancreatic head with elevated CA 19-9 is highly suggestive of pancreatic adenocarcinoma. The pruritus is from bile salt deposition.",
        },
        {
          id: "gen-hard-2",
          patientInfo: "45-year-old male",
          symptoms: [
            "Severe epigastric pain radiating to back",
            "Pain worse after eating and when lying down",
            "Nausea and vomiting",
            "Heavy alcohol use history",
            "Abdominal tenderness with guarding",
          ],
          labResults: "Lipase: markedly elevated (5x normal), CT: pancreatic edema with peripancreatic fluid",
          correctAnswer: "Acute Pancreatitis",
          options: [
            "Acute Pancreatitis",
            "Peptic Ulcer Perforation",
            "Acute Cholecystitis",
            "Mesenteric Ischemia",
          ],
          explanation:
            "Epigastric pain radiating to back, worse supine, with markedly elevated lipase (>3x normal) and CT findings confirm acute pancreatitis. Alcohol is the second most common cause after gallstones.",
        },
        {
          id: "gen-hard-3",
          patientInfo: "70-year-old female",
          symptoms: [
            "Sudden severe abdominal pain out of proportion to exam",
            "Bloody diarrhea",
            "History of atrial fibrillation",
            "Recent heart attack",
            "Abdominal exam initially benign",
          ],
          labResults: "Lactate: elevated, WBC: elevated, CT angiography: occlusion of superior mesenteric artery",
          correctAnswer: "Acute Mesenteric Ischemia",
          options: [
            "Acute Mesenteric Ischemia",
            "Acute Pancreatitis",
            "Perforated Ulcer",
            "Inflammatory Bowel Disease",
          ],
          explanation:
            "Pain out of proportion to physical exam in a patient with atrial fibrillation (embolic risk), elevated lactate, and SMA occlusion on CTA is classic for acute mesenteric ischemia - a vascular emergency.",
        },
        {
          id: "gen-hard-4",
          patientInfo: "58-year-old male",
          symptoms: [
            "Progressive shortness of breath over months",
            "Dry non-productive cough",
            "Fatigue and weight loss",
            "Finger clubbing",
            "History of asbestos exposure",
          ],
          labResults: "Chest X-ray: bilateral reticular opacities, CT chest: honeycombing pattern, PFTs: restrictive pattern with reduced DLCO",
          correctAnswer: "Idiopathic Pulmonary Fibrosis",
          options: [
            "Idiopathic Pulmonary Fibrosis",
            "COPD",
            "Heart Failure",
            "Sarcoidosis",
          ],
          explanation:
            "Progressive dyspnea, dry cough, clubbing, bibasilar crackles with CT showing honeycombing pattern and restrictive PFTs with reduced DLCO are classic for idiopathic pulmonary fibrosis (IPF).",
        },
        {
          id: "gen-hard-5",
          patientInfo: "64-year-old female",
          symptoms: [
            "High fever and chills",
            "Confusion and lethargy",
            "Low blood pressure",
            "Rapid breathing",
            "Recent urinary burning",
          ],
          labResults: "Lactate: elevated, WBC: 18,500/μL, blood cultures: gram-negative rods",
          correctAnswer: "Sepsis (Urosepsis)",
          options: [
            "Sepsis (Urosepsis)",
            "Adrenal Crisis",
            "Heart Failure",
            "Dehydration",
          ],
          explanation:
            "Fever, hypotension, elevated lactate, and bacteremia with urinary symptoms indicate sepsis from a urinary source.",
        },
        {
          id: "gen-hard-6",
          patientInfo: "52-year-old male",
          symptoms: [
            "Severe pain and swelling in leg after minor cut",
            "Rapidly spreading redness",
            "Fever and chills",
            "Pain out of proportion to exam",
            "Skin discoloration with bullae",
          ],
          labResults: "WBC: 22,000/μL, lactate: elevated, CT: fascial gas",
          correctAnswer: "Necrotizing Fasciitis",
          options: [
            "Necrotizing Fasciitis",
            "Cellulitis",
            "DVT",
            "Septic Arthritis",
          ],
          explanation:
            "Severe pain out of proportion, systemic toxicity, and imaging showing fascial gas indicate necrotizing fasciitis, a surgical emergency.",
        },
        {
          id: "gen-hard-7",
          patientInfo: "45-year-old female",
          symptoms: [
            "Severe abdominal pain out of proportion to exam",
            "History of atrial fibrillation",
            "Bloody diarrhea",
            "Abdominal distension",
            "Metabolic acidosis",
          ],
          labResults: "Lactate: markedly elevated, CT angiogram: superior mesenteric artery occlusion",
          correctAnswer: "Acute Mesenteric Ischemia",
          options: [
            "Acute Mesenteric Ischemia",
            "Small Bowel Obstruction",
            "Acute Pancreatitis",
            "Perforated Viscus",
          ],
          explanation:
            "Pain out of proportion, atrial fibrillation (embolic source), elevated lactate, and SMA occlusion indicate acute mesenteric ischemia.",
        },
        {
          id: "gen-hard-8",
          patientInfo: "60-year-old male",
          symptoms: [
            "Painless jaundice",
            "Palpable gallbladder (Courvoisier sign)",
            "Weight loss",
            "New-onset diabetes",
            "Migratory thrombophlebitis",
          ],
          labResults: "CA 19-9: markedly elevated, CT: mass in pancreatic head with biliary dilation",
          correctAnswer: "Pancreatic Cancer",
          options: [
            "Pancreatic Cancer",
            "Cholangiocarcinoma",
            "Ampullary Carcinoma",
            "Chronic Pancreatitis",
          ],
          explanation:
            "Painless jaundice, Courvoisier sign, new diabetes, Trousseau syndrome, and pancreatic head mass indicate pancreatic adenocarcinoma.",
        },
        {
          id: "gen-hard-9",
          patientInfo: "35-year-old female",
          symptoms: [
            "Episodic abdominal pain",
            "Neuropsychiatric symptoms",
            "Red/brown urine during attacks",
            "Constipation",
            "Triggered by certain medications",
          ],
          labResults: "Urine porphobilinogen: markedly elevated during attack, ALA elevated",
          correctAnswer: "Acute Intermittent Porphyria",
          options: [
            "Acute Intermittent Porphyria",
            "Lead Poisoning",
            "Irritable Bowel Syndrome",
            "Conversion Disorder",
          ],
          explanation:
            "Episodic abdominal pain, neuropsychiatric symptoms, dark urine, and elevated PBG indicate acute intermittent porphyria.",
        },
        {
          id: "gen-hard-10",
          patientInfo: "50-year-old male",
          symptoms: [
            "Progressive weakness",
            "Skin hyperpigmentation",
            "Diabetes mellitus",
            "Hepatomegaly with cirrhosis",
            "Arthropathy in MCP joints",
          ],
          labResults: "Ferritin: markedly elevated, Transferrin saturation >45%, Liver biopsy: iron deposition, HFE gene: C282Y homozygous",
          correctAnswer: "Hereditary Hemochromatosis",
          options: [
            "Hereditary Hemochromatosis",
            "Alcoholic Liver Disease",
            "Wilson Disease",
            "Non-Alcoholic Fatty Liver Disease",
          ],
          explanation:
            "Classic triad (cirrhosis, diabetes, skin bronzing) plus iron overload and HFE mutation confirm hereditary hemochromatosis.",
        },
        {
          id: "gen-hard-11",
          patientInfo: "28-year-old male",
          symptoms: [
            "Recurrent deep vein thrombosis",
            "No obvious risk factors",
            "Family history of blood clots",
            "Young age",
            "Clots in unusual locations",
          ],
          labResults: "Factor V Leiden mutation: heterozygous positive, Protein C and S: normal",
          correctAnswer: "Factor V Leiden Thrombophilia",
          options: [
            "Factor V Leiden Thrombophilia",
            "Antiphospholipid Syndrome",
            "Protein C Deficiency",
            "Malignancy-Associated Thrombosis",
          ],
          explanation:
            "Recurrent VTE in young patient with positive Factor V Leiden mutation indicates hereditary thrombophilia.",
        },
        {
          id: "gen-hard-12",
          patientInfo: "65-year-old male",
          symptoms: [
            "Easy bruising and bleeding",
            "Prolonged bleeding after minor procedures",
            "History of liver cirrhosis",
            "Petechiae",
            "Gum bleeding",
          ],
          labResults: "PT/INR: prolonged, aPTT: prolonged, Platelet count: low, Fibrinogen: low",
          correctAnswer: "Disseminated Intravascular Coagulation (DIC)",
          options: [
            "Disseminated Intravascular Coagulation (DIC)",
            "Vitamin K Deficiency",
            "Liver Failure Coagulopathy",
            "Thrombotic Thrombocytopenic Purpura",
          ],
          explanation:
            "Prolonged PT and aPTT, low platelets, low fibrinogen, and elevated D-dimer in setting of underlying disease indicate DIC.",
        },
        {
          id: "gen-hard-13",
          patientInfo: "30-year-old female",
          symptoms: [
            "Microangiopathic hemolytic anemia",
            "Thrombocytopenia",
            "Neurological symptoms",
            "Fever",
            "Renal impairment",
          ],
          labResults: "Platelet count: very low, Schistocytes on smear, LDH: elevated, ADAMTS13 activity: <10%",
          correctAnswer: "Thrombotic Thrombocytopenic Purpura (TTP)",
          options: [
            "Thrombotic Thrombocytopenic Purpura (TTP)",
            "Hemolytic Uremic Syndrome",
            "DIC",
            "ITP",
          ],
          explanation:
            "Classic pentad (MAHA, thrombocytopenia, neurologic, fever, renal) with severely reduced ADAMTS13 confirms TTP.",
        },
        {
          id: "gen-hard-14",
          patientInfo: "55-year-old male",
          symptoms: [
            "Elevated hemoglobin and hematocrit",
            "Facial plethora",
            "Pruritus after hot shower",
            "Splenomegaly",
            "History of thrombosis",
          ],
          labResults: "Hemoglobin: 19 g/dL, Hematocrit: 58%, JAK2 V617F mutation: positive, Low EPO",
          correctAnswer: "Polycythemia Vera",
          options: [
            "Polycythemia Vera",
            "Secondary Polycythemia",
            "Chronic Myeloid Leukemia",
            "Essential Thrombocythemia",
          ],
          explanation:
            "Elevated RBC mass, aquagenic pruritus, splenomegaly, JAK2 mutation, and low EPO indicate polycythemia vera.",
        },
        {
          id: "gen-hard-15",
          patientInfo: "70-year-old male",
          symptoms: [
            "Fatigue and weight loss",
            "Massive splenomegaly",
            "Night sweats",
            "Bone pain",
            "Early satiety",
          ],
          labResults: "WBC: 150,000/μL, Peripheral smear: entire spectrum of myeloid cells, BCR-ABL1 fusion: positive",
          correctAnswer: "Chronic Myeloid Leukemia",
          options: [
            "Chronic Myeloid Leukemia",
            "Chronic Lymphocytic Leukemia",
            "Acute Myeloid Leukemia",
            "Myelofibrosis",
          ],
          explanation:
            "Marked leukocytosis with myeloid cells at all stages of maturation and BCR-ABL1 (Philadelphia chromosome) confirm CML.",
        },
        {
          id: "gen-hard-16",
          patientInfo: "72-year-old male",
          symptoms: [
            "Lymphocytosis discovered incidentally",
            "Painless lymphadenopathy",
            "Fatigue",
            "Recurrent infections",
            "Anemia",
          ],
          labResults: "WBC: 45,000/μL (90% lymphocytes), Smudge cells on smear, Flow cytometry: CD5+, CD19+, CD23+",
          correctAnswer: "Chronic Lymphocytic Leukemia",
          options: [
            "Chronic Lymphocytic Leukemia",
            "Mantle Cell Lymphoma",
            "Hairy Cell Leukemia",
            "Acute Lymphoblastic Leukemia",
          ],
          explanation:
            "Lymphocytosis, smudge cells, and characteristic immunophenotype (CD5+, CD19+, CD23+) confirm CLL.",
        },
        {
          id: "gen-hard-17",
          patientInfo: "45-year-old female",
          symptoms: [
            "Severe headache",
            "Stiff neck",
            "Fever",
            "Photophobia",
            "Altered mental status",
          ],
          labResults: "CSF: elevated WBC (predominantly neutrophils), elevated protein, low glucose, Gram stain: gram-positive diplococci",
          correctAnswer: "Bacterial Meningitis",
          options: [
            "Bacterial Meningitis",
            "Viral Meningitis",
            "Subarachnoid Hemorrhage",
            "Encephalitis",
          ],
          explanation:
            "Meningeal signs with CSF showing neutrophilic pleocytosis, high protein, low glucose, and bacteria confirm bacterial meningitis.",
        },
        {
          id: "gen-hard-18",
          patientInfo: "40-year-old male",
          symptoms: [
            "Progressive weakness",
            "Difficulty breathing",
            "Descending paralysis",
            "Diplopia and dysphagia",
            "Ate home-canned vegetables",
          ],
          labResults: "Normal CSF, EMG: decremental response on low-frequency stimulation, Stool botulinum toxin: positive",
          correctAnswer: "Botulism",
          options: [
            "Botulism",
            "Guillain-Barré Syndrome",
            "Myasthenia Gravis",
            "Lambert-Eaton Syndrome",
          ],
          explanation:
            "Descending paralysis, bulbar symptoms, food exposure history, and positive toxin assay confirm foodborne botulism.",
        },
        {
          id: "gen-hard-19",
          patientInfo: "35-year-old male",
          symptoms: [
            "Painful genital ulcer",
            "Inguinal lymphadenopathy",
            "Recent unprotected sexual contact",
            "Single ulcer with clean base",
            "Indurated edges",
          ],
          labResults: "RPR: reactive, Treponemal antibody (FTA-ABS): positive, HIV test: negative",
          correctAnswer: "Primary Syphilis",
          options: [
            "Primary Syphilis",
            "Herpes Simplex",
            "Chancroid",
            "Lymphogranuloma Venereum",
          ],
          explanation:
            "Painless chancre with indurated edges, inguinal adenopathy, and positive syphilis serology indicate primary syphilis.",
        },
        {
          id: "gen-hard-20",
          patientInfo: "55-year-old female",
          symptoms: [
            "Recurrent fevers",
            "Weight loss",
            "New heart murmur",
            "Splinter hemorrhages",
            "Recent dental procedure",
          ],
          labResults: "Blood cultures: Streptococcus viridans (3/3 positive), Echocardiogram: vegetation on mitral valve",
          correctAnswer: "Subacute Bacterial Endocarditis",
          options: [
            "Subacute Bacterial Endocarditis",
            "Acute Bacterial Endocarditis",
            "Rheumatic Fever",
            "Nonbacterial Thrombotic Endocarditis",
          ],
          explanation:
            "Subacute presentation with viridans streptococci after dental procedure, peripheral stigmata, and valve vegetation indicate SBE.",
        },
      ],
    },
  };

  const caseMeta = Object.keys(allCases).reduce((acc, specialtyId) => {
    const specialtyCases = allCases[specialtyId];
    Object.keys(specialtyCases).forEach((difficultyId) => {
      specialtyCases[difficultyId].forEach((caseItem) => {
        acc[caseItem.id] = { specialtyId, difficultyId };
      });
    });
    return acc;
  }, {});

  useEffect(() => {
    const saved = loadSavedProgress();
    if (!saved) return;

    if (saved.completedCaseIds) {
      setCompletedCaseIds(saved.completedCaseIds);
    }

    const lastSession = saved.lastSession;
    if (lastSession?.selectedSpecialty && lastSession?.selectedDifficulty) {
      setResumeSession(lastSession);
    }
  }, []);

  const handleResumeSession = () => {
    if (!resumeSession) return;

    const caseMap = Object.keys(allCases).reduce((acc, specialtyId) => {
      const levels = allCases[specialtyId];
      Object.keys(levels).forEach((difficultyId) => {
        levels[difficultyId].forEach((caseItem) => {
          acc[caseItem.id] = caseItem;
        });
      });
      return acc;
    }, {});

    const orderedCases = (resumeSession.activeCaseIds || [])
      .map((caseId) => caseMap[caseId])
      .filter(Boolean);
    const restoredCases = orderedCases.length > 0 ? orderedCases : [];
    const restoredIndex = Math.min(
      resumeSession.caseIndex || 0,
      Math.max(restoredCases.length - 1, 0),
    );

    setSelectedSpecialty(resumeSession.selectedSpecialty);
    setSelectedDifficulty(resumeSession.selectedDifficulty);
    setActiveCases(restoredCases);
    setCaseIndex(restoredIndex);
    setCurrentCase(restoredCases[restoredIndex] || restoredCases[0] || null);
    setScore(resumeSession.score || 0);
    setCasesCompleted(resumeSession.casesCompleted || 0);
    setSelectedAnswer(resumeSession.selectedAnswer || null);
    setShowResult(Boolean(resumeSession.showResult));
    setGamePhase(resumeSession.gamePhase || "playing");
  };

  const handleClearResumeSession = () => {
    const nextProgress = {
      completedCaseIds,
      lastSession: null,
    };
    setResumeSession(null);
    saveProgress(nextProgress);
  };

  useEffect(() => {
    const nextProgress = {
      completedCaseIds,
      lastSession: null,
    };

    if (selectedSpecialty && selectedDifficulty && activeCases.length > 0) {
      nextProgress.lastSession = {
        selectedSpecialty,
        selectedDifficulty,
        gamePhase,
        caseIndex,
        score,
        casesCompleted,
        activeCaseIds: activeCases.map((caseItem) => caseItem.id),
        selectedAnswer,
        showResult,
      };
    }

    setResumeSession(nextProgress.lastSession);
    saveProgress(nextProgress);
  }, [
    completedCaseIds,
    selectedSpecialty,
    selectedDifficulty,
    gamePhase,
    caseIndex,
    score,
    casesCompleted,
    activeCases,
    selectedAnswer,
    showResult,
  ]);

  const getSpecialtyProgress = (specialtyId) => {
    let total = 0;
    let completed = 0;

    if (specialtyId === "random") {
      Object.values(allCases).forEach((levels) => {
        Object.values(levels).forEach((cases) => {
          total += cases.length;
          cases.forEach((caseItem) => {
            if (completedCaseIds[caseItem.id]) completed += 1;
          });
        });
      });
      return { total, completed };
    }

    const levels = allCases[specialtyId] || {};
    Object.values(levels).forEach((cases) => {
      total += cases.length;
      cases.forEach((caseItem) => {
        if (completedCaseIds[caseItem.id]) completed += 1;
      });
    });

    return { total, completed };
  };

  const getDifficultyProgress = (difficultyId) => {
    let total = 0;
    let completed = 0;

    if (selectedSpecialty === "random") {
      Object.keys(allCases).forEach((specialtyId) => {
        const cases = allCases[specialtyId]?.[difficultyId] || [];
        total += cases.length;
        cases.forEach((caseItem) => {
          if (completedCaseIds[caseItem.id]) completed += 1;
        });
      });
      return { total, completed };
    }

    const cases = allCases[selectedSpecialty]?.[difficultyId] || [];
    total = cases.length;
    cases.forEach((caseItem) => {
      if (completedCaseIds[caseItem.id]) completed += 1;
    });

    return { total, completed };
  };

  const handleSpecialtySelect = (specialtyId) => {
    setSelectedSpecialty(specialtyId);
    setGamePhase("difficulty");
  };

  const handleDifficultySelect = (difficultyId) => {
    setSelectedDifficulty(difficultyId);

    // Prepare cases based on specialty and difficulty
    let cases = [];

    if (selectedSpecialty === "random") {
      // Mix cases from all specialties at the selected difficulty
      Object.keys(allCases).forEach((specialty) => {
        if (allCases[specialty][difficultyId]) {
          cases = [...cases, ...allCases[specialty][difficultyId]];
        }
      });
      // Shuffle the random mix
      cases = cases.sort(() => Math.random() - 0.5);
    } else {
      // Get cases for specific specialty
      cases = allCases[selectedSpecialty]?.[difficultyId] || [];
    }

    setActiveCases(cases);
    setGamePhase("playing");
    setCaseIndex(0);
    setScore(0);
    setCasesCompleted(0);
    loadCase(cases, 0);
  };

  const loadCase = (cases, index) => {
    if (index < cases.length) {
      setCurrentCase(cases[index]);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      saveUserData({ ...userData, avatarStats: applyBoost(userData.avatarStats, GAME_BOOST) });
      setGamePhase("complete");
    }
  };

  const handleAnswerSelect = (answer) => {
    if (!showResult) {
      setSelectedAnswer(answer);
    }
  };

  const handleSubmit = () => {
    if (selectedAnswer) {
      setShowResult(true);
      const isCorrect = selectedAnswer === currentCase.correctAnswer;

      if (currentCase?.id && caseMeta[currentCase.id]) {
        setCompletedCaseIds((prev) =>
          prev[currentCase.id] ? prev : { ...prev, [currentCase.id]: true },
        );
      }

      if (isCorrect) {
        setScore(score + 1);
        // Award coins and XP based on difficulty
        const difficulty = difficultyLevels.find(
          (d) => d.id === selectedDifficulty,
        );
        const newUserData = {
          ...userData,
          coins: userData.coins + difficulty.coins,
          xp: userData.xp + difficulty.xp,
        };
        saveUserData(newUserData);
      }

      setCasesCompleted(casesCompleted + 1);
    }
  };

  const handleNext = () => {
    const nextIndex = caseIndex + 1;
    setCaseIndex(nextIndex);
    loadCase(activeCases, nextIndex);
  };

  const handleRestart = () => {
    setGamePhase("specialty");
    setSelectedSpecialty(null);
    setSelectedDifficulty(null);
    setScore(0);
    setCasesCompleted(0);
    setCaseIndex(0);
    setActiveCases([]);
  };

  // Specialty Selection Screen
  if (gamePhase === "specialty") {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-detective.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-teal-900/80 to-blue-900/80">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <button
              onClick={onBack}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Games
            </button>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-6">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Symptom Detective
              </h1>
              <p className="text-gray-600">
                Choose your medical specialty to begin diagnosing cases
              </p>
            </div>
            {resumeSession && (
              <div className="mb-6 flex flex-wrap gap-3">
                <button
                  onClick={handleResumeSession}
                  className="bg-teal-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors w-full md:w-auto"
                >
                  Resume last session
                </button>
                <button
                  onClick={handleClearResumeSession}
                  className="bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-colors w-full md:w-auto"
                >
                  Clear saved session
                </button>
              </div>
            )}

            <h2 className="text-2xl font-bold text-white mb-6">
              Select Specialty
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {specialties.map((specialty) => {
                const SpecialtyIcon = specialty.icon;
                const { total, completed } = getSpecialtyProgress(specialty.id);
                const progressPercent = total > 0 ? (completed / total) * 100 : 0;
                return (
                  <button
                    key={specialty.id}
                    onClick={() => handleSpecialtySelect(specialty.id)}
                    className={
                      "rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br " +
                      specialty.color +
                      " " +
                      specialty.borderColor
                    }
                  >
                    <div className="flex justify-center mb-4">
                      <div className="bg-white bg-opacity-80 rounded-full p-4 shadow-md">
                        <SpecialtyIcon
                          className={"w-8 h-8 " + specialty.iconColor}
                        />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
                      {specialty.name}
                    </h3>
                    <p className="text-sm text-gray-600 text-center">
                      {specialty.description}
                    </p>
                    <div className="mt-4">
                      <div className="h-2 bg-white/70 rounded-full">
                        <div
                          className="h-full bg-teal-600 rounded-full transition-all duration-300"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                      <div className="mt-1 text-xs text-gray-600 text-center">
                        {completed} / {total} cases completed
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Difficulty Selection Screen
  if (gamePhase === "difficulty") {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-detective.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-teal-900/80 to-blue-900/80">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <button
              onClick={() => setGamePhase("specialty")}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Specialties
            </button>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-6">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Select Difficulty Level
              </h1>
              <p className="text-gray-600">
                Specialty:{" "}
                {specialties.find((s) => s.id === selectedSpecialty)?.name}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {difficultyLevels.map((level) => {
                const { total, completed } = getDifficultyProgress(level.id);
                const progressPercent = total > 0 ? (completed / total) * 100 : 0;
                return (
                  <button
                    key={level.id}
                    onClick={() => handleDifficultySelect(level.id)}
                    className={
                      "rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br " +
                      level.color +
                      " " +
                      level.borderColor
                    }
                  >
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {level.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {level.description}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-teal-600">
                        {level.coins} coins/case
                      </span>
                      <span className="font-semibold text-purple-600">
                        {level.xp} XP/case
                      </span>
                    </div>
                    <div className="mt-4">
                      <div className="h-2 bg-white/70 rounded-full">
                        <div
                          className="h-full bg-teal-600 rounded-full transition-all duration-300"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                      <div className="mt-1 text-xs text-gray-600 text-center">
                        {completed} / {total} cases completed
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>{" "}
      </div>
    );
  }

  // Game Complete Screen
  if (gamePhase === "complete") {
    const percentage =
      activeCases.length > 0
        ? Math.round((score / activeCases.length) * 100)
        : 0;
    const difficulty = difficultyLevels.find(
      (d) => d.id === selectedDifficulty,
    );

    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-detective.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-teal-900/80 to-blue-900/80">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <button
              onClick={onBack}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Games
            </button>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center">
              <Award className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                Game Complete!
              </h2>
              <p className="text-gray-600 mb-6">
                Specialty:{" "}
                {specialties.find((s) => s.id === selectedSpecialty)?.name} •
                Level: {difficulty?.name}
              </p>

              <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 mb-6">
                <div className="text-5xl font-bold text-teal-600 mb-2">
                  {score}/{activeCases.length}
                </div>
                <div className="text-gray-700 text-lg mb-2">
                  {percentage}% Correct
                </div>
                <div className="text-sm text-gray-600">
                  You earned {score * difficulty.coins} coins and{" "}
                  {score * difficulty.xp} XP!
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleRestart}
                  className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-5 h-5" />
                  Play Again
                </button>
                <button
                  onClick={onBack}
                  className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Back to Games
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Playing Phase
  if (!currentCase) {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-detective.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-teal-900/80 to-blue-900/80">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="text-center text-white">Loading cases...</div>
          </div>
        </div>
      </div>
    );
  }

  const isCorrect = showResult && selectedAnswer === currentCase.correctAnswer;
  const difficulty = difficultyLevels.find((d) => d.id === selectedDifficulty);

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: "url('/avatar-detective.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="min-h-screen bg-gradient-to-br from-teal-900/80 to-blue-900/80">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={() => setGamePhase("difficulty")}
            className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
          >
            ← Change Difficulty
          </button>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {specialties.find((s) => s.id === selectedSpecialty)?.name}
                </h1>
                <p className="text-sm text-gray-600">
                  {difficulty?.name} Level
                </p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">
                  Case {caseIndex + 1} of {activeCases.length}
                </div>
                <div className="text-lg font-bold text-teal-600">
                  Score: {score}/{casesCompleted}
                </div>
              </div>
            </div>

            <div className="h-2 bg-gray-200 rounded-full mb-4">
              <div
                className="h-full bg-teal-600 rounded-full transition-all duration-300"
                style={{
                  width: `${((caseIndex + 1) / activeCases.length) * 100}%`,
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-2">
                Patient Information
              </h2>
              <p className="text-gray-700">{currentCase.patientInfo}</p>
            </div>

            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-3">
                Presenting Symptoms
              </h2>
              <ul className="space-y-2">
                {currentCase.symptoms.map((symptom, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-gray-700"
                  >
                    <span className="text-teal-600 mt-1">•</span>
                    <span>{symptom}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h2 className="text-lg font-bold text-gray-800 mb-2">
                Lab Results / Diagnostic Tests
              </h2>
              <p className="text-gray-700 font-mono text-sm">
                {currentCase.labResults}
              </p>
            </div>

            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-3">
                What is your diagnosis?
              </h2>
              <div className="space-y-3">
                {currentCase.options.map((option, index) => {
                  const isSelected = selectedAnswer === option;
                  const isCorrectOption = option === currentCase.correctAnswer;

                  let buttonClass =
                    "w-full text-left p-4 rounded-lg border-2 transition-all ";

                  if (showResult) {
                    if (isCorrectOption) {
                      buttonClass += "bg-green-50 border-green-500 ";
                    } else if (isSelected && !isCorrectOption) {
                      buttonClass += "bg-red-50 border-red-500 ";
                    } else {
                      buttonClass += "bg-gray-50 border-gray-200 opacity-50 ";
                    }
                  } else {
                    if (isSelected) {
                      buttonClass += "bg-teal-50 border-teal-500 ";
                    } else {
                      buttonClass +=
                        "bg-white border-gray-300 hover:border-teal-400 hover:bg-teal-50 ";
                    }
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(option)}
                      disabled={showResult}
                      className={buttonClass}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-800">
                          {option}
                        </span>
                        {showResult && isCorrectOption && (
                          <Check className="w-5 h-5 text-green-600" />
                        )}
                        {showResult && isSelected && !isCorrectOption && (
                          <X className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {showResult && (
              <div
                className={
                  "p-4 rounded-lg border-2 mb-6 " +
                  (isCorrect
                    ? "bg-green-50 border-green-500"
                    : "bg-red-50 border-red-500")
                }
              >
                <div className="flex items-center gap-2 mb-2">
                  {isCorrect ? (
                    <Check className="w-6 h-6 text-green-600" />
                  ) : (
                    <X className="w-6 h-6 text-red-600" />
                  )}
                  <h3 className="font-bold text-gray-800">
                    {isCorrect ? "Correct!" : "Incorrect"}
                  </h3>
                </div>
                {isCorrect && (
                  <p className="text-sm text-green-700 mb-2">
                    +{difficulty.coins} coins, +{difficulty.xp} XP
                  </p>
                )}
                <p className="text-sm text-gray-700 font-semibold mb-2">
                  Correct Answer: {currentCase.correctAnswer}
                </p>
                <p className="text-sm text-gray-700">
                  {currentCase.explanation}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              {!showResult ? (
                <button
                  onClick={handleSubmit}
                  disabled={!selectedAnswer}
                  className={
                    "flex-1 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 " +
                    (selectedAnswer
                      ? "bg-teal-600 text-white hover:bg-teal-700"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed")
                  }
                >
                  Submit Answer
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="flex-1 bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
                >
                  Next Case
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>{" "}
      </div>{" "}
    </div>
  );
};

export default SymptomDetective;
